import React, { useState, useRef, useEffect } from 'react';
import PDFGenerator from '../utils/PDFGenerator';
import settingsService from '../services/settingsService';
import { useSettings } from '../contexts/SettingsContext';
import { performGlobalSearch } from '../services/searchAPI';
import cnp2021 from '../data/cnp2021.json';
import './VisaVisitorDetails.css';
import {
  VisitorChecklistAnswers, VISITOR_RELATIONSHIPS, VISITOR_INVITER_STATUSES,
  visitorHasPartner, isVisitorInvitationComplete, getVisitorFamilyDocuments, getVisitorInvitationDocuments
} from '../utils/visitorDocuments';

type CnpRecord = { code: string; title: string; teer: string };
const CNP_2021 = cnp2021 as CnpRecord[];
const normalizeSearchTerm = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const GUARANTOR_OPTIONS = [
  { value: 'lui-meme', label: 'L\'étudiant lui-même' },
  { value: 'mere', label: 'Mère' },
  { value: 'pere', label: 'Père' },
  { value: 'frere-soeur', label: 'Frère/Sœur' },
  { value: 'oncle-tante', label: 'Oncle/Tante' },
  { value: 'autre', label: 'Autre membre de la famille' }
];

const hasPositiveCount = (value?: string) => Number.parseInt(value || '0', 10) > 0;

interface Responses extends VisitorChecklistAnswers {
  visaType?: string[];
  
  // Étude
  travelMode?: string;
  accompaniedBy?: string[];
  childrenCount?: string;
  studentResidedAbroad?: string;
  studyLocation?: string;
  guarantors?: string[];
  spouseResidedAbroad?: string;
  spouseHasProperty?: string;
  spouseStatus?: string[];
  spouseFunds?: string[];
  spouseHasFunds?: string;
  singleParentTravel?: string;
  additionalDocs?: string[];

  // Visiteur
  visitorVisaType?: 'individuel' | 'famille';
  visitorFamilyMembers?: string[];
  visitorPrincipal?: {
    hasProperty?: 'oui' | 'non';
    status?: string[];
    hasGuarantor?: 'oui' | 'non';
    funds?: string[];
  };
  visitorSpouse?: {
    hasProperty?: 'oui' | 'non';
    status?: string[];
    hasFunds?: 'oui' | 'non';
    funds?: string[];
  };
  visitorGuarantor?: {
    hasProperty?: 'oui' | 'non';
    status?: string[];
    funds?: string[];
  };
  visitorChildrenCount?: string;
  visitorSingleParent?: { [key: number]: 'oui' | 'non' };

  // Entrée Express
  eeResidedAbroad?: 'oui' | 'non';
  eeProgram?: 'fsw' | 'cec' | 'fst';
  eeEducationProof?: 'canadian' | 'eca' | 'none';
  eeHasJobOffer?: 'oui' | 'non';
  eeHasProvincialNomination?: 'oui' | 'non';
  eeHasTradeCertificate?: 'oui' | 'non';
  eeFundsExempt?: 'oui' | 'non';
  eeUsesRepresentative?: 'oui' | 'non';
  eeJobCount?: string;
  eeJobs?: { [key: number]: { type?: 'salarie' | 'entrepreneur' } };
  eeMaritalStatus?: 'single' | 'married' | 'common-law' | 'divorced' | 'widowed';
  eeIsMarried?: 'oui' | 'non';
  eeHasChildren?: string;
  eeSpouseAccompanying?: 'oui' | 'non';
  eeChildrenAccompanying?: 'oui' | 'non';
  eeSpouseResidedAbroad?: 'oui' | 'non';
  eeChildrenCount?: string;
  eeSingleParentTravel?: 'oui' | 'non';
  eeHasFrenchTest?: 'oui' | 'non';
  eeHasEnglishTest?: 'oui' | 'non';
  eeHasFamilyInCanada?: 'oui' | 'non';
  eeSpouseHasFrenchTest?: 'oui' | 'non';
  eeSpouseHasEnglishTest?: 'oui' | 'non';
  eeSpouseHasFamilyInCanada?: 'oui' | 'non';

  // Immigration permanente au Québec (MIFI)
  quebecProgram?: 'pstq' | 'peq-travailleur' | 'peq-diplome' | 'autre';
  qcPstqInvited?: 'oui' | 'non';
  qcPstqStream?: '1' | '2' | '3' | '4' | 'inconnu';
  qcPrincipalOccupation?: string;
  qcCnpCode?: string;
  qcCnpSearch?: string;
  qcFrenchProof?: 'oui' | 'non';
  qcPeqWorkerExperience?: 'oui' | 'non';
  qcPeqGraduateCredential?: 'oui' | 'non';
  qcHasAccompanyingFamily?: 'oui' | 'non';

  [key: string]: any;
}

const VisaDocumentGenerator = () => {
  const { settings } = useSettings();
  const [currentStep, setCurrentStep] = useState<string>('visa-type');
  const [stepHistory, setStepHistory] = useState<string[]>([]);
  const [responses, setResponses] = useState<Responses>({});
  const [documentList, setDocumentList] = useState<string[]>([]);
  
  // États pour la recherche client/lead
  const [personQuery, setPersonQuery] = useState('');
  const [personResults, setPersonResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effet pour la recherche avec debounce
  useEffect(() => {
    if (!personQuery || personQuery.trim().length < 2) {
      setPersonResults([]);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await performGlobalSearch(personQuery.trim());
        const data = res?.data || {};
        const leads = (data.leads || []).map((l: any) => ({
          id: l.id,
          type: 'lead',
          label: l.title || l.subtitle || 'Lead',
          raw: l
        }));
        const clients = (data.clients || []).map((c: any) => ({
          id: c.id,
          type: 'client',
          label: c.title || c.subtitle || 'Client',
          raw: c
        }));
        setPersonResults([...clients.slice(0, 5), ...leads.slice(0, 5)]);
      } catch (err) {
        console.error('Erreur recherche:', err);
        setPersonResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [personQuery]);

  const getGuarantorLabel = (value: string) => {
    const option = GUARANTOR_OPTIONS.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const resetForm = () => {
    setCurrentStep('visa-type');
    setStepHistory([]);
    setResponses({});
    setDocumentList([]);
  };

  const handleResponse = (key: string, value: any) => {
    const keys = key.split('.');
    if (keys.length > 1) {
      setResponses(prev => {
        const newState = { ...prev };
        let current: any = newState;
        for (let i = 0; i < keys.length - 1; i++) {
          current[keys[i]] = current[keys[i]] || {};
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return newState;
      });
    } else {
      const newResponses = { ...responses, [key]: value };
      setResponses(newResponses);
    }
  };

  const setEEJobType = (index: number, type: 'salarie' | 'entrepreneur') => {
    setResponses((previous) => ({
      ...previous,
      eeJobs: {
        ...(previous.eeJobs || {}),
        [index]: {
          ...(previous.eeJobs?.[index] || {}),
          type
        }
      }
    }));
  };

  const generateDocumentList = () => {
    // Every visitor path must collect family status and invitation details before generation.
    if (responses.visaType?.includes('2') && !responses.visaType.includes('1')) {
      if (!responses.visitorMaritalStatus) { nextStep('visitor-marital-status'); return; }
      if (visitorHasPartner(responses) && !responses.visitorSpouseAccompanying) { nextStep('visitor-spouse-accompanying'); return; }
      if (!responses.visitorVisaType) { nextStep('visitor-type'); return; }
      if (!isVisitorInvitationComplete(responses)) { nextStep('visitor-canada-invitation'); return; }
    }
    let docs: string[] = [];
    
    // Documents généraux pour étude
    if (responses.visaType && responses.visaType.includes('1')) {
      docs = [
        "=== DOCUMENTS POUR L'ÉTUDIANT PRINCIPAL ===",
        "1. Formulaire de demande de visa pour études, rempli et signé",
        "2. Formulaire d'information sur la famille, rempli",
        "3. Copie du passeport de l'étudiant",
        "4. Copie des visas de l'étudiant avec les cachets d'entrée et sorties",
        "5. Acte de naissance de l'étudiant",
        "6. Lettre d'invitation de la province (LAP)",
        "7. Lettre d'admission, baccalauréat, relevé de notes du baccalauréat, attestation de réussite après le baccalauréat et tout autre diplôme avec ses relevés de notes associés",
        "8. Photo d'identité récente"
      ];

      // Documents selon la résidence de l'étudiant
      if (responses.studentResidedAbroad === 'oui') {
        docs.push("9. Casier judiciaire du pays de résidence (original + traduction en français ou en anglais, datant de moins de 3 mois)");
        docs.push("10. Fiche anthropométrique du pays d'origine (récente, moins de 3 mois)");
      } else {
        docs.push("9. Fiche anthropométrique du pays d'origine (récente, moins de 3 mois)");
      }

      docs.push("10. Preuve du lien de parenté avec le garant (actes de naissance en français ou traduits)");

      // CAQ si Québec
      if (responses.studyLocation === 'quebec') {
        docs.push("11. CAQ (Certificat d'acceptation du Québec)");
      }

      // Lettre de prise en charge
      if (responses.guarantors && responses.guarantors.length > 0) {
        responses.guarantors.forEach((guarantor: string) => {
          docs.push(`12. Lettre de prise en charge (${getGuarantorLabel(guarantor)})`);
        });
      }

      // Documents selon le statut des garants
      Object.entries(responses).forEach(([key, value]) => {
        if (key.startsWith('guarantor_') && key.includes('_status')) {
          const guarantorName = key.split('_')[1];
          
          if (Array.isArray(value)) {
            value.forEach((status: string) => {
              addDocumentsForStatus(docs, status, guarantorName);
            });
          }
        }
      });

      // Documents de preuve de fonds
      Object.entries(responses).forEach(([key, value]) => {
        if (key.startsWith('guarantor_') && key.includes('_funds')) {
          const guarantorName = key.split('_')[1];
          
          if (Array.isArray(value)) {
            value.forEach((fundType: string) => {
              addFundDocuments(docs, fundType, guarantorName);
            });
          }
        }
      });

      // Documents de propriété des garants
      Object.entries(responses).forEach(([key, value]) => {
        if (key.startsWith('guarantor_') && key.includes('_property') && value === 'oui') {
          const guarantorName = key.split('_')[1];
          const guarantorLabel = getGuarantorLabel(guarantorName);
          docs.push(`Attestation de propriété en langue d'origine (récente, mieux 3 derniers mois) - ${guarantorLabel}`);
          docs.push(`Traduction en français ou en anglais de cette attestation - ${guarantorLabel}`);
        }
      });

      // Documents pour époux/épouse
      if (responses.accompaniedBy && responses.accompaniedBy.includes('spouse')) {
        docs.push("\n=== DOCUMENTS POUR L'ÉPOUX/ÉPOUSE ===");
        docs.push("• Acte de mariage langue d'origine + traduction en français ou anglais");
        docs.push("• Photo");
        if (responses.studyLocation === 'quebec') {
          docs.push("• CAQ");
        }
        docs.push("• Passeport avec cachets d'entrée et de sortie et visas");

        // Documents selon la résidence de l'époux/épouse
        if (responses.spouseResidedAbroad === 'oui') {
          docs.push("• Fiche anthropométrique du pays d'origine avec traduction en français ou en anglais");
          docs.push("• Casier judiciaire de ce pays avec traduction en français ou en anglais");
        } else if (responses.spouseResidedAbroad === 'non') {
          docs.push("• Fiche anthropométrique du pays d'origine avec traduction en français ou en anglais");
        }

        // Documents de propriété de l'époux/épouse
        if (responses.spouseHasProperty === 'oui') {
          docs.push("• Actes de propriété en langue d'origine");
          docs.push("• Traduction en français ou en anglais de chaque acte");
        }

        // Documents selon le statut de l'époux/épouse
        if (responses.spouseStatus && responses.spouseStatus.length > 0) {
          responses.spouseStatus.forEach((status: string) => {
            addVisitorDocumentsForStatus(docs, status, "Époux/Épouse");
          });
        }

        // Documents de fonds de l'époux/épouse
        if (responses.spouseFunds && responses.spouseFunds.length > 0) {
          responses.spouseFunds.forEach((fundType: string) => {
            addVisitorFundDocuments(docs, fundType, "Époux/Épouse");
          });
        }
      }

      // Documents pour enfants
      if (responses.accompaniedBy && responses.accompaniedBy.includes('children') && responses.childrenCount) {
        docs.push("\n=== DOCUMENTS POUR LES ENFANTS ===");
        for (let i = 1; i <= parseInt(responses.childrenCount); i++) {
          docs.push(`--- Enfant ${i} ---`);
          if (responses.studyLocation === 'quebec') {
            docs.push(`• CAQ - Enfant ${i}`);
          }
          docs.push(`• Photo d'identité - Enfant ${i}`);
          docs.push(`• Acte de naissance - Enfant ${i}`);
          docs.push(`• Passeport avec cachets d'entrée et de sortie et visas - Enfant ${i}`);
        }

        // Autorisation parentale si un seul parent
        if (responses.singleParentTravel === 'oui') {
          docs.push("• Autorisation de voyage de l'autre parent");
        }
      }

      // Documents complémentaires
      if (responses.additionalDocs && responses.additionalDocs.length > 0) {
        docs.push("\n=== DOCUMENTS COMPLÉMENTAIRES ===");
        responses.additionalDocs.forEach((doc: string) => {
          docs.push(`• ${getAdditionalDocText(doc)}`);
        });
      }
    } else if (responses.visaType && responses.visaType.includes('2')) {
      // Logique pour le visa visiteur
      if (responses.visitorVisaType === 'individuel') {
        docs.push("=== DOCUMENTS POUR VISA VISITEUR - INDIVIDUEL ===");
        docs.push("• Formulaire de visa visiteur interne rempli et signé");
        docs.push("• Copie du passeport du requérant principal");
        docs.push("• Copie des visas avec les cachets d'entrée et sorties");
        docs.push("• Copie de CIN");
        docs.push("• Photo");

        if (responses.visitorPrincipal?.hasProperty === 'oui') {
          docs.push("• Attestation de propriété en langue d'origine (récente, mieux 3 derniers mois)");
          docs.push("• Traduction en français ou en anglais de cette attestation");
        }
        if (responses.visitorPrincipal?.status) {
          responses.visitorPrincipal.status.forEach(stat => addVisitorDocumentsForStatus(docs, stat, "Requérant Principal"));
        }
        if (responses.visitorPrincipal?.funds) {
          responses.visitorPrincipal.funds.forEach(fund => addVisitorFundDocuments(docs, fund, "Requérant Principal"));
        }

        const visitorTravelGuarantors: string[] = (responses as any).visitorTravelGuarantors || [];
        const otherGuarantors = visitorTravelGuarantors.filter((g) => g !== 'Lui-même');
        if (otherGuarantors.length > 0) {
          otherGuarantors.forEach((g) => {
            docs.push(`• Lettre de prise en charge - ${g}`);
          });

          otherGuarantors.forEach((g) => {
            docs.push(`• Preuve de lien entre le demandeur et le garant - ${g}`);
          });

          const guarantorsBiens: Record<string, string> = (responses as any).visitorGuarantorsBiens || {};
          const guarantorsStatus: Record<string, string[]> = (responses as any).visitorGuarantorsStatus || {};
          const guarantorsFunds: Record<string, string[]> = (responses as any).visitorGuarantorsFunds || {};

          otherGuarantors.forEach((g) => {
            const personLabel = `Garant (${g})`;
            if (guarantorsBiens[g] === 'oui') {
              docs.push(`• Attestation de propriété en langue d'origine (récente, mieux 3 derniers mois) - ${personLabel}`);
              docs.push(`• Traduction en français ou en anglais de cette attestation - ${personLabel}`);
            }
            (guarantorsStatus[g] || []).forEach((stat) => addVisitorDocumentsForStatus(docs, stat, personLabel));
            (guarantorsFunds[g] || []).forEach((fund) => addVisitorFundDocuments(docs, fund, personLabel));
          });
        }

      } else if (responses.visitorVisaType === 'famille') {
        docs.push("=== DOCUMENTS POUR VISA VISITEUR - FAMILLE ===");
        
        if (responses.visitorFamilyMembers?.includes('principal')) {
          docs.push("\n--- REQUÉRANT PRINCIPAL ---");
          docs.push("• Formulaire de visa visiteur interne rempli et signé");
          docs.push("• Copie du passeport");
          docs.push("• Copie des visas avec les cachets d'entrée et sorties");
          docs.push("• Copie de CIN");
          docs.push("• Photo");
          if (responses.visitorPrincipal?.hasProperty === 'oui') {
            docs.push("• Attestation de propriété en langue d'origine (récente, mieux 3 derniers mois)");
            docs.push("• Traduction en français ou en anglais de cette attestation");
          }
          if (responses.visitorPrincipal?.status) {
            responses.visitorPrincipal.status.forEach(stat => addVisitorDocumentsForStatus(docs, stat, "Requérant Principal"));
          }
          if (responses.visitorPrincipal?.funds) {
            responses.visitorPrincipal.funds.forEach(fund => addVisitorFundDocuments(docs, fund, "Requérant Principal"));
          }
        }
        
        if (responses.visitorFamilyMembers?.includes('spouse')) {
          docs.push("\n--- ÉPOUX/ÉPOUSE ---");
           if (responses.visitorSpouse?.hasProperty === 'oui') {
            docs.push("• Attestation de propriété en langue d'origine (récente, mieux 3 derniers mois)");
            docs.push("• Traduction en français ou en anglais de cette attestation");
          }
          if (responses.visitorSpouse?.status) {
            responses.visitorSpouse.status.forEach(stat => addVisitorDocumentsForStatus(docs, stat, "Époux/Épouse"));
          }
          if (responses.visitorSpouse?.funds) {
            responses.visitorSpouse.funds.forEach(fund => addVisitorFundDocuments(docs, fund, "Époux/Épouse"));
          }
        }

        if (responses.visitorFamilyMembers?.includes('children') && responses.visitorChildrenCount) {
          docs.push("\n--- ENFANTS ---");
          for (let i = 1; i <= parseInt(responses.visitorChildrenCount); i++) {
            docs.push(`-- Enfant ${i} --`);
            docs.push(`• Attestation de scolarité ou d'inscription`);
            docs.push(`• Copie du passeport + visas et cachets entrée et sorties`);
            docs.push(`• Acte de naissance`);
            docs.push(`• Photo`);
          }
          if (responses.visitorSingleParent === 'oui') {
            docs.push("• Autorisation de voyage de l'autre parent");
          }
        }

      }
      docs.push(...getVisitorFamilyDocuments(responses), ...getVisitorInvitationDocuments(responses));
    } else if (responses.visaType && responses.visaType.includes('3')) {
      docs.push("=== DOCUMENTS POUR IMMIGRATION - ENTRÉE EXPRESS ===");
      docs.push("--- REQUÉRANT PRINCIPAL ---");
      docs.push("• Copie du passeport");
      docs.push("• Copie des visas et des caches entrées et sorties");
      docs.push("• Copie de CIN");
      docs.push("• photo");
      docs.push("• diplômes");
      docs.push("• releves de notes");
      docs.push("• Examen médical d'immigration (IME) auprès d'un médecin désigné");
      docs.push("• Certificats de police pour chaque pays requis");
      docs.push("• Traductions certifiées et affidavit du traducteur, si applicable");
      docs.push("• Lettre d'explication pour tout document indisponible ou information particulière, si applicable");

      if (responses.eeEducationProof === 'canadian') {
        docs.push("• Diplôme ou attestation d'études canadien");
      } else if (responses.eeEducationProof === 'eca') {
        docs.push("• Évaluation des diplômes d'études (EDE) valide");
      }

      if (responses.eeResidedAbroad === 'oui') {
        docs.push("• Fiche de police de ce pays langue d'origine + traduction en français ou anglais, récente (3 mois)");
        docs.push("• Fiche anthropométrique du pays d'origine récente (3 mois)");
      } else {
        docs.push("• Fiche anthropométrique du pays d'origine récente (3 mois)");
      }

      if (responses.eeHasFrenchTest === 'oui') {
        docs.push("• copie du test de francais valide");
      }
      if (responses.eeHasEnglishTest === 'oui') {
        docs.push("• copie du test d'anglais valide");
      }
      if (responses.eeHasFamilyInCanada === 'oui') {
        docs.push("• preuve de residence ou de citoyeneté canadienne");
        docs.push("• preuve de lien de parenté");
      }
      if (responses.eeHasJobOffer === 'oui') {
        docs.push("• Offre d'emploi écrite et documents d'appui de l'employeur, si applicable");
      }
      if (responses.eeHasProvincialNomination === 'oui') {
        docs.push("• Certificat de désignation provinciale");
      }
      if (responses.eeHasTradeCertificate === 'oui') {
        docs.push("• Certificat de qualification délivré par une province ou un territoire canadien");
      }
      if (responses.eeFundsExempt !== 'oui') {
        docs.push("• Preuve de fonds disponible et transférable selon la taille de la famille");
      }
      if (responses.eeUsesRepresentative === 'oui') {
        docs.push("• Formulaire Recours aux services d'un représentant (IMM 5476)");
      }
      if (responses.eeMaritalStatus === 'divorced') {
        docs.push("• Certificat de divorce et accord de séparation, si applicable");
      }
      if (responses.eeMaritalStatus === 'widowed') {
        docs.push("• Certificat de décès de l'époux/épouse");
      }

      if (responses.eeJobCount && responses.eeJobs) {
        for (let i = 0; i < parseInt(responses.eeJobCount); i++) {
          const job = responses.eeJobs[i];
          if (job && job.type) {
            docs.push(`--- Emploi ${i + 1} (${job.type}) ---`);
            if (job.type === 'salarie') {
              docs.push("• attestation de travail");
              docs.push("• fiche de poste");
              docs.push("• Historique de CNSS");
            } else if (job.type === 'entrepreneur') {
              docs.push("• Statut");
              docs.push("• RC si entrepreneur");
              docs.push("• attestation de travail");
              docs.push("• fiche de poste");
            }
          }
        }
      }

      if ((responses.eeMaritalStatus === 'married' || responses.eeMaritalStatus === 'common-law') && responses.eeSpouseAccompanying === 'oui') {
        docs.push("\n--- ÉPOUX/ÉPOUSE ---");
        docs.push("• Copie du passeport");
        docs.push("• Copie des visas et des caches entrées et sorties");
        docs.push("• Copie de CIN");
        docs.push("• photo");
        docs.push("• diplômes");
        docs.push("• releves de notes");
        docs.push("• equivalence");
        docs.push("• Acte de mariage en langue d'origine");
        docs.push("• Traduction de l'acte de mariage et français ou en anglais");
        if (responses.eeMaritalStatus === 'common-law') {
          docs.push("• Déclaration officielle d'union de fait (IMM 5409) et preuves de cohabitation");
        }
        
        if (responses.eeSpouseResidedAbroad === 'oui') {
          docs.push("• Fiche de police de ce pays langue d'origine + traduction en français ou anglais, récente (3 mois)");
          docs.push("• Fiche anthropométrique du pays d'origine récente (3 mois)");
        } else {
          docs.push("• Fiche anthropométrique du pays d'origine récente (3 mois)");
        }

        if (responses.eeSpouseHasFrenchTest === 'oui') {
          docs.push("• copie du test de francais valide");
        }
        if (responses.eeSpouseHasEnglishTest === 'oui') {
          docs.push("• copie du test d'anglais valide");
        }
        if (responses.eeSpouseHasFamilyInCanada === 'oui') {
          docs.push("• preuve de residence ou de citoyeneté canadienne");
          docs.push("• preuve de lien de parenté");
        }
      }

      if (hasPositiveCount(responses.eeHasChildren) && responses.eeChildrenAccompanying === 'oui' && hasPositiveCount(responses.eeChildrenCount)) {
        docs.push("\n--- ENFANTS ACCOMPAGNANT(S) ---");
        for (let i = 0; i < parseInt(responses.eeChildrenCount); i++) {
          docs.push(`-- Enfant ${i + 1} --`);
          docs.push("• Copie du passeport + visas et cachés entrée et sorties");
          docs.push("• Acte de naissance");
          docs.push("• Photo");
        }
        if (responses.eeSingleParentTravel === 'oui') {
          docs.push("• Autorisation de voyage de l'autre parent pour chaque enfant");
        }
      }
    } else if (responses.visaType && responses.visaType.includes('4')) {
      docs.push("=== IMMIGRATION PERMANENTE - QUÉBEC (MIFI) ===");
      docs.push("Cette liste est une préparation interne. La liste finale à téléverser est celle affichée dans Arrima pour votre dossier.");

      if (responses.quebecProgram === 'pstq') {
        docs.push("\n--- PSTQ : DÉCLARATION D'INTÉRÊT / DEMANDE APRÈS INVITATION ---");
        if (responses.qcPrincipalOccupation) docs.push(`• Profession principale déclarée : ${responses.qcPrincipalOccupation}${responses.qcCnpCode ? ` (CNP 2021 : ${responses.qcCnpCode})` : ''}.`);
        docs.push("Les pièces ci-dessous sont à préparer selon votre situation déclarée. Elles ne doivent être téléversées dans Arrima qu'après invitation du MIFI.");
        docs.push("\n--- DOCUMENTS D'IDENTITÉ ET DE SÉJOUR ---");
        docs.push("• Passeport valide : page d'identité et toutes les pages comportant un visa, un permis, un tampon ou une annotation.");
        docs.push("• Acte de naissance / certificat de naissance du requérant principal et de chaque membre de la famille déclaré.");
        docs.push("• Permis de travail et permis d'études IRCC obtenus au Canada au cours des 10 dernières années, incluant les prolongations de statut, si applicable.");
        docs.push("• Titres de séjour ou permis de résidence temporaire dans tout autre pays au cours des 10 dernières années, si applicable.");
        docs.push("• Traduction certifiée de tout document qui n'est ni en français ni en anglais, accompagnée du document original.");
        docs.push("\n--- ÉTUDES ET FORMATION ---");
        docs.push("• Diplôme final pour chaque formation déclarée ainsi que les relevés de notes de chaque année menant au diplôme.");
        docs.push("• Attestation de l'établissement indiquant la durée de la formation si elle n'apparaît pas sur les documents fournis.");
        docs.push("• Liste officielle des cours si les intitulés ne figurent pas sur les relevés de notes.");
        docs.push("• Pour une formation à distance : attestation officielle du registraire précisant les modalités de la formation, si applicable.");
        docs.push("\n--- EXPÉRIENCE PROFESSIONNELLE : À FOURNIR POUR CHAQUE EMPLOI DÉCLARÉ DES 5 DERNIÈRES ANNÉES ---");
        docs.push("• Curriculum vitæ complet et à jour, avec les dates, employeurs, fonctions et périodes sans emploi, le cas échéant.");
        docs.push("• Attestation d'emploi originale sur papier à en-tête de l'employeur, datée et signée à la main, avec le nom et les coordonnées du signataire.");
        docs.push("• L'attestation doit préciser : titre du poste, dates de début et de fin, lieu de travail, nombre d'heures par semaine, salaire, postes occupés, dates correspondantes et tâches principales.");
        docs.push("• Au moins 6 fiches de paie couvrant le début, le milieu et la fin de la période d'emploi réclamée, lorsque disponibles.");
        docs.push("• Preuves de cotisations sociales ou fiscales : relevés T4/Relevé 1, avis de cotisation ou document équivalent selon le pays, si applicable.");
        docs.push("• Permis, licence ou autorisation professionnelle lorsque l'exercice de la profession l'exige.");
        docs.push("\n--- SI VOUS ÊTES TRAVAILLEUR AUTONOME / ENTREPRENEUR ---");
        docs.push("• Avis de cotisation d'impôt personnel couvrant la période déclarée.");
        docs.push("• Contrats de services représentatifs de la période de travail autonome déclarée.");
        docs.push("• Attestations de clients précisant les services, heures et rémunérations; factures émises et preuves de paiement.");
        docs.push("• Relevés bancaires personnels ou professionnels montrant les revenus liés à l'activité.");
        docs.push("• Certificat d'immatriculation de l'entreprise, inscription au registre du commerce ou publication officielle équivalente.");
        docs.push("• Avis de cotisation de l'entreprise, carte d'identification fiscale et licence professionnelle, si applicables.");
        docs.push("\n--- SI VOUS DÉCLAREZ UN STAGE ---");
        docs.push("• Attestation de stage indiquant les dates, les tâches, le nombre d'heures, le lieu, les coordonnées du superviseur, sa signature et la date.");
        docs.push("• Convention de stage ou attestation officielle de l'établissement d'enseignement.");
        docs.push("\n--- FRANÇAIS ET CONDITIONS PSTQ ---");
        docs.push("• Résultats ou attestation de français acceptés par le MIFI pour le requérant principal et, s'il accompagne, pour l'époux ou le conjoint de fait, selon le volet PSTQ.");
        docs.push("• Contrat d'autonomie financière exigé par le MIFI lors de la demande de sélection permanente.");
        docs.push("• Attestation d'apprentissage des valeurs démocratiques et des valeurs québécoises pour les personnes concernées de 18 ans ou plus.");
        if (responses.qcPstqInvited === 'non') {
          docs.push("• Déclaration d'intérêt Arrima : identité, études, profession principale, expérience et français déclarés avec exactitude.");
          docs.push("• La demande de sélection permanente et le téléversement officiel des pièces ne sont effectués qu'après invitation dans Arrima.");
        } else {
          docs.push("• Invitation Arrima et preuve de son acceptation dans le délai de 30 jours prévu par le MIFI.");
          docs.push("• Tous les documents doivent être numérisés en couleur, lisibles et regroupés en un seul PDF par exigence, conformément aux instructions Arrima.");
        }
        if (responses.qcFrenchProof === 'oui') docs.push("• Joindre le résultat de français déclaré dans Arrima, en vérifiant qu'il fait partie des preuves acceptées par le MIFI.");
        if (responses.qcPstqStream === '3') {
          docs.push("\n--- VOLET 3 : PROFESSIONS RÉGLEMENTÉES ---");
          docs.push("• Document de l'autorité de réglementation du Québec : permis d'exercice, preuve d'admissibilité, reconnaissance d'équivalence complète ou partielle, selon le cas.");
          docs.push("• Le document de l'autorité doit être valide et récent conformément aux exigences du MIFI.");
        }
        if (responses.qcPstqStream === '4') {
          docs.push("\n--- VOLET 4 : TALENTS D'EXCEPTION ---");
          docs.push("• Avis positif d'un partenaire du MIFI ou preuve de l'accomplissement exceptionnel reconnu pour ce volet.");
        }
      } else if (responses.quebecProgram === 'peq-travailleur') {
        docs.push("\n--- PEQ - TRAVAILLEUR ÉTRANGER TEMPORAIRE ---");
        docs.push("• Passeport, permis de travail et preuves de statut au Québec.");
        docs.push("• Attestation d'emploi actuelle et preuves du travail légal, rémunéré et à temps plein au Québec.");
        docs.push("• Contrats, fiches de paie et relevés fiscaux ou sociaux attestant l'expérience déclarée.");
        docs.push("• Preuve de connaissance du français selon les conditions du PEQ.");
      } else if (responses.quebecProgram === 'peq-diplome') {
        docs.push("\n--- PEQ - DIPLÔMÉ DU QUÉBEC ---");
        docs.push("• Passeport et preuves de statut au Québec.");
        docs.push("• Diplôme admissible obtenu au Québec, relevés de notes et documents de l'établissement selon la situation.");
        docs.push("• Preuve de connaissance du français selon les conditions du PEQ.");
      } else {
        docs.push("• Programme à confirmer avec le MIFI avant toute collecte de documents ou dépôt dans Arrima.");
      }

      if (responses.qcHasAccompanyingFamily === 'oui') {
        docs.push("\n--- MEMBRES DE LA FAMILLE À DÉCLARER ---");
        docs.push("• Passeports et actes de naissance des personnes à charge.");
        docs.push("• Certificat de mariage, union de fait ou autres preuves de lien familial, avec traduction certifiée si nécessaire.");
        docs.push("• Documents de garde ou consentement parental pour un enfant mineur, si applicable.");
      }

      docs.push("\n--- APRÈS LE CSQ : ÉTAPE FÉDÉRALE IRCC ---");
      docs.push("• La demande de résidence permanente fédérale est distincte : certificats de police, examen médical, biométrie et formulaires sont fournis à cette étape, selon les instructions d'IRCC.");
    } else if (responses.visaType && responses.visaType.includes('4')) {
      // Immigration - Québec: même logique qu'Entrée Express mais sans l'équivalence (EDE)
      const startIndex = docs.length;
      // Reutiliser la génération Entrée Express en simulant le même bloc
      const saved = { ...responses };
      // On suppose que les mêmes champs ee* sont utilisés
      // Générer en copiant la structure Entrée Express
      // Pour éviter la duplication importante de code, on peut rappeler la branche 3 en factorisant, mais ici on ré-applique minimalement
      docs.push("=== DOCUMENTS POUR IMMIGRATION - QUÉBEC ===");
      docs.push("--- REQUÉRANT PRINCIPAL ---");
      docs.push("• Copie du passeport");
      docs.push("• Copie des visas et des caches entrées et sorties");
      docs.push("• Copie de CIN");
      docs.push("• Preuve de fond selon le nombre de la famille");
      docs.push("• photo");
      docs.push("• diplômes");
      docs.push("• releves de notes");
      // pas d'équivalence pour Québec

      if (responses.eeResidedAbroad === 'oui') {
        docs.push("• Fiche de police de ce pays langue d'origine + traduction en français ou anglais, récente (3 mois)");
        docs.push("• Fiche anthropométrique du pays d'origine récente (3 mois)");
      } else {
        docs.push("• Fiche anthropométrique du pays d'origine récente (3 mois)");
      }

      if (responses.eeHasFrenchTest === 'oui') {
        docs.push("• copie du test de francais valide");
      }
      if (responses.eeHasEnglishTest === 'oui') {
        docs.push("• copie du test d'anglais valide");
      }
      if (responses.eeHasFamilyInCanada === 'oui') {
        docs.push("• preuve de residence ou de citoyeneté canadienne");
        docs.push("• preuve de lien de parenté");
      }

      if (responses.eeJobCount && responses.eeJobs) {
        for (let i = 0; i < parseInt(responses.eeJobCount); i++) {
          const job = responses.eeJobs[i];
          if (job && job.type) {
            docs.push(`--- Emploi ${i + 1} (${job.type}) ---`);
            if (job.type === 'salarie') {
              docs.push("• attestation de travail");
              docs.push("• fiche de poste");
              docs.push("• Historique de CNSS");
            } else if (job.type === 'entrepreneur') {
              docs.push("• Statut");
              docs.push("• RC si entrepreneur");
              docs.push("• attestation de travail");
              docs.push("• fiche de poste");
            }
          }
        }
      }

      if ((responses.eeMaritalStatus === 'married' || responses.eeMaritalStatus === 'common-law') && responses.eeSpouseAccompanying === 'oui') {
        docs.push("\n--- ÉPOUX/ÉPOUSE ---");
        docs.push("• Copie du passeport");
        docs.push("• Copie des visas et des caches entrées et sorties");
        docs.push("• Copie de CIN");
        docs.push("• photo");
        docs.push("• diplômes");
        docs.push("• releves de notes");
        // pas d'"equivalence" pour le conjoint non plus
        docs.push("• Acte de mariage en langue d'origine");
        docs.push("• Traduction de l'acte de mariage et français ou en anglais");
        if (responses.eeMaritalStatus === 'common-law') {
          docs.push("• Déclaration officielle d'union de fait (IMM 5409) et preuves de cohabitation");
        }
        
        if (responses.eeSpouseResidedAbroad === 'oui') {
          docs.push("• Fiche de police de ce pays langue d'origine + traduction en français ou anglais, récente (3 mois)");
          docs.push("• Fiche anthropométrique du pays d'origine récente (3 mois)");
        } else {
          docs.push("• Fiche anthropométrique du pays d'origine récente (3 mois)");
        }

        if (responses.eeSpouseHasFrenchTest === 'oui') {
          docs.push("• copie du test de francais valide");
        }
        if (responses.eeSpouseHasEnglishTest === 'oui') {
          docs.push("• copie du test d'anglais valide");
        }
        if (responses.eeSpouseHasFamilyInCanada === 'oui') {
          docs.push("• preuve de residence ou de citoyeneté canadienne");
          docs.push("• preuve de lien de parenté");
        }
      }

      if (hasPositiveCount(responses.eeHasChildren) && responses.eeChildrenAccompanying === 'oui' && hasPositiveCount(responses.eeChildrenCount)) {
        docs.push("\n--- ENFANTS ACCOMPAGNANT(S) ---");
        for (let i = 0; i < parseInt(responses.eeChildrenCount); i++) {
          docs.push(`-- Enfant ${i + 1} --`);
          docs.push("• Copie du passeport + visas et cachés entrée et sorties");
          docs.push("• Acte de naissance");
          docs.push("• Photo");
        }
        if (responses.eeSingleParentTravel === 'oui') {
          docs.push("• Autorisation de voyage de l'autre parent pour chaque enfant");
        }
      }
    }

    setDocumentList(docs);
    setCurrentStep('final');
  };

  const addDocumentsForStatus = (docs: string[], status: string, guarantorName: string) => {
    const guarantorLabel = getGuarantorLabel(guarantorName);
    
    // Document obligatoire pour tous les garants
    docs.push(`Relevés bancaires des 4 derniers mois et attestation de solde - ${guarantorLabel}`);
    
    switch (status) {
      case 'salarie':
        docs.push(`Attestation de travail - ${guarantorLabel}`);
        docs.push(`Attestation de salaire - ${guarantorLabel}`);
        docs.push(`Bulletin de paie 3 mois - ${guarantorLabel}`);
        break;
      case 'entrepreneur_sans_salaire':
        docs.push(`Statut de l'entreprise - ${guarantorLabel}`);
        docs.push(`Registre de commerce (récent de 3 mois) - ${guarantorLabel}`);
        docs.push(`Preuve de paiement des impôts - ${guarantorLabel}`);
        docs.push(`Attestation du solde de l'entreprise - ${guarantorLabel}`);
        docs.push(`Relevé bancaire de l'entreprise des 4 derniers mois - ${guarantorLabel}`);
        docs.push(`Déclaration sur l'honneur avec les dividendes annuels - ${guarantorLabel}`);
        break;
      case 'entrepreneur_avec_salaire':
        docs.push(`Attestation de travail - ${guarantorLabel}`);
        docs.push(`Attestation de salaire - ${guarantorLabel}`);
        docs.push(`Bulletins de paie 3 mois - ${guarantorLabel}`);
        docs.push(`Statut de l'entreprise - ${guarantorLabel}`);
        docs.push(`Registre de commerce (récent de 3 mois) - ${guarantorLabel}`);
        docs.push(`Preuve de paiement des impôts - ${guarantorLabel}`);
        docs.push(`Attestation du solde de l'entreprise - ${guarantorLabel}`);
        docs.push(`Relevé bancaire de l'entreprise des 4 derniers mois - ${guarantorLabel}`);
        docs.push(`Déclaration sur l'honneur avec les dividendes annuels hors salaire - ${guarantorLabel}`);
        break;
      case 'agriculteur':
        docs.push(`Acte de propriété des terrains ou contrat de bail des terrains - ${guarantorLabel}`);
        docs.push(`Déclaration sur l'honneur avec le revenu annuel ou mensuel de cette activité - ${guarantorLabel}`);
        break;
      case 'retraite':
        docs.push(`Attestation de retraite (CNSS ou CNOPS ou les deux ou autre si hors Maroc) - ${guarantorLabel}`);
        break;
    }
  };

  const addSpouseDocumentsForStatus = (docs: string[], status: string) => {
    // NOTE: Cette fonction est obsolète et neutralisée pour éviter la duplication de documents.
    // La logique est maintenant gérée par addVisitorDocumentsForStatus.
  };

  const addFundDocuments = (docs: string[], fundType: string, guarantorName: string) => {
    // NOTE: Cette fonction est obsolète et neutralisée pour éviter la duplication de documents.
    // La logique est maintenant gérée par addVisitorFundDocuments.
  };

  const addSpouseFundDocuments = (docs: string[], fundType: string) => {
    // NOTE: Cette fonction est obsolète et neutralisée pour éviter la duplication de documents.
    // La logique est maintenant gérée par addVisitorFundDocuments.
  };

  const getAdditionalDocText = (doc: string) => {
    const texts = {
      'frais_scolarite': 'Preuve de paiement des frais de scolarité (total ou partiel)',
      'certificat_garantie': 'Certificat de garantie au Canada (pour les frais de subsistance de la première année d\'études)',
      'invitation_bail': 'Lettre d\'invitation ou contrat de bail',
      'promesse_embauche': 'Promesse d\'embauche hors Canada après l\'obtention du diplôme',
      'reussites': 'Toute autre preuve de réussite de l\'étudiant (personnelle, associative, professionnelle ou scolaire)',
      'attestation_blocage': 'Attestation de blocage des fonds pour les frais de subsistance de la première année'
    };
    return texts[doc as keyof typeof texts] || doc;
  };

  const nextStep = (step: string) => {
    setStepHistory(prev => [...prev, currentStep]);
    setCurrentStep(step);
  };

  const previousStep = () => {
    setStepHistory(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setCurrentStep(last);
      return prev.slice(0, -1);
    });
  };

  const handleMultipleChoice = (key: string, value: string, checked: boolean) => {
    const keys = key.split('.');
    if (keys.length > 1) {
      setResponses(prev => {
        const newState = { ...prev };
        let current: any = newState;
        for (let i = 0; i < keys.length - 1; i++) {
          current[keys[i]] = current[keys[i]] || {};
          current = current[keys[i]];
        }
        
        const currentValues = current[keys[keys.length - 1]] || [];
        const updated = checked
          ? [...currentValues, value]
          : currentValues.filter((item: string) => item !== value);
        current[keys[keys.length - 1]] = updated;
        return newState;
      });
    } else {
      const current = (responses[key] as string[]) || [];
      const updated = checked
        ? [...current, value]
        : current.filter((item: string) => item !== value);
      setResponses({ ...responses, [key]: updated });
    }
  };

  const handleMultipleChoiceInMap = (
    mapKey: string,
    mapItemKey: string,
    value: string,
    checked: boolean
  ) => {
    const currentMap = (responses as any)[mapKey] || {};
    const currentValues: string[] = currentMap[mapItemKey] || [];
    const updatedValues = checked
      ? [...currentValues, value]
      : currentValues.filter((item: string) => item !== value);
    setResponses({
      ...responses,
      [mapKey]: {
        ...currentMap,
        [mapItemKey]: updatedValues
      }
    });
  };

  const downloadList = () => {
    // Le logo doit toujours provenir de l'entreprise de l'utilisateur connecté.
    // /api/logo est une ancienne route globale : elle renvoie le dernier fichier
    // ajouté sur le serveur et peut donc afficher le logo d'une autre entreprise.
    const logoUrl = settingsService.getLogoUrl(true, settings.logo);
    const clientName = selectedPerson ? selectedPerson.label : null;

    PDFGenerator.genererDepuisTexte(documentList, 'liste-documents-visa.pdf', logoUrl, clientName);
  };

  const getNextStepAfterVisaType = () => {
    if (responses.visaType && responses.visaType.includes('1')) {
      return 'travel-alone';
    }
    if (responses.visaType && responses.visaType.includes('2')) {
      return 'visitor-marital-status';
    }
    if (responses.visaType && responses.visaType.includes('4') && !responses.visaType.includes('3')) {
      return 'qc-program';
    }
    // Entrée Express conserve son propre flux de questions.
    if (responses.visaType && responses.visaType.includes('3')) {
      return 'ee-residence';
    }
    return 'final'; // Pour les autres types de visa pour l'instant
  };

  const addVisitorDocumentsForStatus = (docs: string[], status: string, person: string) => {
    const documentsToAdd = [];
    switch (status) {
      case 'salarie':
        documentsToAdd.push(`• Attestation de travail - ${person}`);
        documentsToAdd.push(`• Attestation de salaire - ${person}`);
        documentsToAdd.push(`• Bulletin de paie 3 mois - ${person}`);
        documentsToAdd.push(`• Relevé bancaire des 4 derniers mois - ${person}`);
        break;
      case 'entrepreneur_sans_salaire':
        documentsToAdd.push(`• Statut de l'entreprise - ${person}`);
        documentsToAdd.push(`• Registre de commerce (récent de 3 mois) - ${person}`);
        documentsToAdd.push(`• Preuve de paiement des impôts - ${person}`);
        documentsToAdd.push(`• Attestation du solde de l'entreprise - ${person}`);
        documentsToAdd.push(`• Relevé bancaire de l'entreprise des 4 derniers mois - ${person}`);
        documentsToAdd.push(`• Déclaration sur l'honneur avec les dividendes annuels - ${person}`);
        break;
      case 'entrepreneur_avec_salaire':
        documentsToAdd.push(`• Attestation de travail - ${person}`);
        documentsToAdd.push(`• Attestation de salaire - ${person}`);
        documentsToAdd.push(`• Bulletins de paie 3 mois - ${person}`);
        documentsToAdd.push(`• Statut de l'entreprise - ${person}`);
        documentsToAdd.push(`• Registre de commerce (récent de 3 mois) - ${person}`);
        documentsToAdd.push(`• Preuve de paiement des impôts - ${person}`);
        documentsToAdd.push(`• Attestation du solde de l'entreprise - ${person}`);
        documentsToAdd.push(`• Relevé bancaire de l'entreprise des 4 derniers mois - ${person}`);
        documentsToAdd.push(`• Déclaration sur l'honneur avec les dividendes annuels hors salaire - ${person}`);
        break;
      case 'agriculteur':
        documentsToAdd.push(`• Acte de propriété des terrains ou contrat de bail des terrains - ${person}`);
        documentsToAdd.push(`• Déclaration sur l'honneur avec le revenu annuel ou mensuel de cette activité - ${person}`);
        break;
      case 'retraite':
        documentsToAdd.push(`• Attestation de retraite (CNSS ou CNOPS ou autre) - ${person}`);
        break;
      case 'etudiant':
        documentsToAdd.push(`• Attestation de scolarité ou d'inscription - ${person}`);
        break;
      case 'femme_foyer':
        break;
    }
    documentsToAdd.forEach(doc => {
      if (!docs.includes(doc)) {
        docs.push(doc);
      }
    });
  };

  const addVisitorFundDocuments = (docs: string[], fundType: string, person: string) => {
    const documentsToAdd = [];
    switch (fundType) {
      case 'epargne':
        documentsToAdd.push(`• Attestation de solde - ${person}`);
        documentsToAdd.push(`• Relevé bancaire des 6 ou 12 derniers mois - ${person}`);
        break;
      case 'heritage':
        documentsToAdd.push(`• Attestation de solde - ${person}`);
        documentsToAdd.push(`• Relevé bancaire des 4 derniers mois - ${person}`);
        documentsToAdd.push(`• Preuve d'héritage - ${person}`);
        break;
      case 'vente_bien':
        documentsToAdd.push(`• Attestation de vente des biens - ${person}`);
        documentsToAdd.push(`• Attestation de solde - ${person}`);
        documentsToAdd.push(`• Relevé bancaire des 4 derniers mois - ${person}`);
        break;
    }
    documentsToAdd.forEach(doc => {
      if (!docs.includes(doc)) {
        docs.push(doc);
      }
    });
  };

  const getPstqCnpGuidance = () => {
    const code = String(responses.qcCnpCode || '').replace(/\D/g, '');
    if (code.length !== 5) return null;

    const teer = code.charAt(1);
    if (['0', '1', '2'].includes(teer)) {
      return {
        teer,
        recommendation: 'Volet 1 – Haute qualification et compétences spécialisées',
        detail: 'Le niveau FEER du code CNP indique généralement le volet 1. Les autres conditions PSTQ doivent tout de même être vérifiées.'
      };
    }
    if (['3', '4', '5'].includes(teer)) {
      return {
        teer,
        recommendation: 'Volet 2 – Compétences intermédiaires et manuelles',
        detail: 'Le niveau FEER du code CNP indique généralement le volet 2. Les autres conditions PSTQ doivent tout de même être vérifiées.'
      };
    }
    return null;
  };

  const renderEEChoiceStep = (
    step: string,
    title: string,
    responseKey: string,
    options: Array<{ value: string; label: string; description?: string }>,
    next: string
  ) => {
    if (currentStep !== step) return null;

    return (
      <div className="visa-step-container">
        <div className="visa-step-header">
          <h2 className="visa-step-title">{title}</h2>
        </div>
        <div className="visa-grid">
          {options.map((option) => (
            <label key={option.value} className="visa-option-card">
              <input
                type="radio"
                name={responseKey}
                value={option.value}
                checked={responses[responseKey] === option.value}
                onChange={(e) => handleResponse(responseKey, e.target.value)}
                className="visa-radio"
              />
              <div className="visa-card-content">
                <div>
                  <h3 className="visa-card-title">{option.label}</h3>
                  {option.description && <p className="visa-card-description">{option.description}</p>}
                </div>
              </div>
            </label>
          ))}
        </div>
        <div className="visa-step-actions">
          <button onClick={() => nextStep(next)} disabled={!responses[responseKey]} className="visa-button-primary">
            Continuer <span className="button-arrow">→</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`visa-document-generator${responses.visaType?.includes('2') ? ' visa-visitor-flow' : ''}`}>
      {/* Header Section */}
      <div className="visa-header">
        <div className="visa-header-content">
          <div className="visa-header-title-container">
            <div className="visa-header-icon">
              <span>📋</span>
            </div>
            <div>
              <h1 className="visa-header-title">
                Générateur de Documents Visa
              </h1>
              <p className="visa-header-subtitle">
                Assistant intelligent pour générer votre liste de documents personnalisée
              </p>
            </div>
          </div>
          {currentStep !== 'visa-type' && (
            <button 
              onClick={resetForm}
              className="visa-button-secondary"
            >
              <span className="button-icon">↺</span>
              Recommencer
            </button>
          )}
          {currentStep !== 'visa-type' && currentStep !== 'final' && (
            <button 
              onClick={previousStep}
              className="visa-button-secondary"
            >
              <span className="button-arrow">←</span>
              Précédent
            </button>
          )}
        </div>
        
        {/* Progress Bar */}
        <div className="progress-container">
          <div 
            className="progress-bar"
            style={{
              width: currentStep === 'visa-type' ? '10%' : 
                     currentStep === 'travel-mode' ? '20%' :
                     currentStep === 'accompanied-by' ? '30%' :
                     currentStep === 'children-count' ? '40%' :
                     currentStep === 'residence-question' ? '50%' :
                     currentStep === 'study-location' ? '60%' :
                     currentStep === 'guarantors' ? '70%' :
                     currentStep === 'additional-questions' ? '90%' :
                     '100%'
            }}
          ></div>
        </div>

        {/* Barre de recherche client/lead */}
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
            Générer pour un client ou un lead (optionnel)
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={selectedPerson ? selectedPerson.label : personQuery}
              onChange={(e) => { setSelectedPerson(null); setPersonQuery(e.target.value); }}
              placeholder="Rechercher par nom, email ou téléphone..."
              style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '6px', padding: '10px 12px' }}
            />
            {(personQuery && !selectedPerson) && (
              <div style={{ position: 'absolute', zIndex: 10, top: '44px', left: 0, right: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', maxHeight: '260px', overflowY: 'auto', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                {isSearching && (
                  <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280' }}>Recherche...</div>
                )}
                {!isSearching && personResults.length === 0 && (
                  <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280' }}>Aucun résultat</div>
                )}
                {personResults.map((r) => (
                  <div
                    key={`${r.type}-${r.id}`}
                    onClick={() => { setSelectedPerson(r); setPersonQuery(''); }}
                    style={{ padding: '10px 12px', fontSize: '14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                  >
                    <span>{r.label}</span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{r.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedPerson && (
            <div style={{ marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#f3f4f6', borderRadius: '999px', padding: '6px 10px', fontSize: '13px', color: '#374151' }}>
              <span style={{ fontWeight: 600 }}>{selectedPerson.type === 'client' ? 'Client' : 'Lead'}:</span>
              <span>{selectedPerson.label}</span>
              <button onClick={() => setSelectedPerson(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '18px', lineHeight: 1 }}>×</button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Card */}
      <div className="visa-content-card slide-up">
        {currentStep === 'visa-type' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Quelle démarche d'immigration souhaitez-vous préparer ?</h2>
              <p className="visa-step-subtitle">Choisissez une démarche à la fois afin d'obtenir des questions et une liste de documents adaptés.</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: '1', label: 'Études', icon: '🎓', desc: 'Pour poursuivre des études au Canada' },
                { value: '2', label: 'Visiteur', icon: '✈️', desc: 'Pour visiter le Canada temporairement' },
                { value: '3', label: 'Immigration - Entrée Express', icon: '🏠', desc: 'Programme fédéral d\'immigration permanente' },
                { value: '4', label: 'Immigration permanente - Québec', icon: '🍁', desc: 'PSTQ, PEQ ou autre voie permanente relevant du MIFI' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="checkbox"
                    checked={(responses.visaType || []).includes(option.value)}
                    onChange={(e) => handleResponse('visaType', e.target.checked ? [option.value] : [])}
                    className="visa-checkbox"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">{option.icon}</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">{option.desc}</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            
            <div className="visa-step-actions">
              <button
                onClick={() => nextStep(getNextStepAfterVisaType())}
                disabled={!responses.visaType || responses.visaType.length === 0}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'travel-alone' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Mode de voyage</h2>
              <p className="visa-step-subtitle">L'étudiant voyage-t-il seul ou accompagné ?</p>
            </div>
            
            <div className="visa-grid">
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="travelMode"
                  value="seul"
                  onChange={(e) => handleResponse('travelMode', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content-vertical">
                  <div className="visa-card-icon-large">👤</div>
                  <h3 className="visa-card-title">Seul</h3>
                  <p className="visa-card-description">L'étudiant voyage sans accompagnateur.</p>
                </div>
              </label>
              
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="travelMode"
                  value="accompagne"
                  onChange={(e) => handleResponse('travelMode', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content-vertical">
                  <div className="visa-card-icon-large">👥</div>
                  <h3 className="visa-card-title">Accompagné</h3>
                  <p className="visa-card-description">L'étudiant voyage avec des accompagnateurs.</p>
                </div>
              </label>
            </div>
            
            <div className="visa-step-actions">
              <button
                onClick={() => nextStep(responses.travelMode === 'accompagne' ? 'accompanied-by' : 'residence-question')}
                disabled={!responses.travelMode}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'accompanied-by' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Qui accompagne l'étudiant ?</h2>
              <p className="visa-step-subtitle">(Plusieurs choix possibles)</p>
            </div>
            
            <div className="visa-grid">
              <label className="visa-option-card">
                <input
                  type="checkbox"
                  onChange={(e) => handleMultipleChoice('accompaniedBy', 'spouse', e.target.checked)}
                  className="visa-checkbox"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">👫</div>
                  <div>
                    <h3 className="visa-card-title">Époux/Épouse</h3>
                    <p className="visa-card-description">L'étudiant voyage avec son conjoint.</p>
                  </div>
                </div>
              </label>
              <label className="visa-option-card">
                <input
                  type="checkbox"
                  onChange={(e) => handleMultipleChoice('accompaniedBy', 'children', e.target.checked)}
                  className="visa-checkbox"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">👶</div>
                  <div>
                    <h3 className="visa-card-title">Enfants</h3>
                    <p className="visa-card-description">L'étudiant voyage avec ses enfants.</p>
                  </div>
                </div>
              </label>
            </div>

            {responses.accompaniedBy && responses.accompaniedBy.includes('children') && (
              <div style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                borderRadius: '16px',
                border: '2px solid #90caf9'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #2196f3 0%, #9c27b0 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '1rem',
                    fontSize: '24px',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                  }}>
                    👶
                  </div>
                  <div>
                    <label style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#1976d2',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Nombre d'enfants qui voyageront
                    </label>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#5e35b1',
                      margin: 0
                    }}>
                      Précisez combien d'enfants accompagneront le voyageur
                    </p>
                  </div>
                </div>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={responses.childrenCount || ''}
                  onChange={(e) => handleResponse('childrenCount', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    border: '2px solid #90caf9',
                    borderRadius: '12px',
                    background: 'white',
                    color: '#1976d2',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
                  }}
                  placeholder="Entrez"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2196f3';
                    e.target.style.boxShadow = '0 4px 16px rgba(33, 150, 243, 0.3)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#90caf9';
                    e.target.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.1)';
                  }}
                />
              </div>
            )}

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('residence-question')}
                disabled={!responses.accompaniedBy || responses.accompaniedBy.length === 0}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'residence-question' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">L'étudiant a-t-il résidé plus de 6 mois dans un autre pays ?</h2>
              <p className="visa-step-subtitle">Indiquez si l'étudiant a résidé plus de 6 mois dans un autre pays avant son arrivée au Canada.</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: 'oui', label: 'Oui' },
                { value: 'non', label: 'Non' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="radio"
                    name="studentResidence"
                    value={option.value}
                    onChange={(e) => handleResponse('studentResidedAbroad', e.target.value)}
                    className="visa-radio"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">🌍</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez si l'étudiant a résidé plus de 6 mois dans un autre pays.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('study-location')}
                disabled={!responses.studentResidedAbroad}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'study-location' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Lieu des études</h2>
              <p className="visa-step-subtitle">Où l'étudiant compte-t-il poursuivre ses études ?</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: 'quebec', label: 'Au Québec' },
                { value: 'hors-quebec', label: 'Hors Québec' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="radio"
                    name="location"
                    value={option.value}
                    onChange={(e) => handleResponse('studyLocation', e.target.value)}
                    className="visa-radio"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">🏫</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez où l'étudiant souhaite étudier.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('guarantors')}
                disabled={!responses.studyLocation}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'guarantors' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Qui sont les garants ?</h2>
              <p className="visa-step-subtitle">(Plusieurs choix possibles)</p>
            </div>
            
            <div className="visa-grid">
              {GUARANTOR_OPTIONS.map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="checkbox"
                    onChange={(e) => handleMultipleChoice('guarantors', option.value, e.target.checked)}
                    className="visa-checkbox"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">👪</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez le garant de l'étudiant.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('guarantor-status')}
                disabled={!responses.guarantors || responses.guarantors.length === 0}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'guarantor-status' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Statut de chaque garant</h2>
              <p className="visa-step-subtitle">Indiquez le statut de chaque garant (salarié, entrepreneur, etc.).</p>
            </div>
            
            <div className="visa-grid">
              {responses.guarantors && responses.guarantors.map(guarantor => (
                <div key={guarantor} className="visa-option-card">
                  <h3 className="visa-card-title">Statut du garant : {getGuarantorLabel(guarantor)}</h3>
                  <div className="visa-grid">
                    {[
                      { value: 'salarie', label: 'Salarié' },
                      { value: 'entrepreneur_avec_salaire', label: 'Entrepreneur avec salaire' },
                      { value: 'entrepreneur_sans_salaire', label: 'Entrepreneur sans salaire' },
                      { value: 'agriculteur', label: 'Agriculteur' },
                      { value: 'proprietaire_location', label: 'Propriétaire de bien en location' },
                      { value: 'retraite', label: 'Retraité' },
                      { value: 'heritage', label: 'Héritage' }
                    ].map(option => (
                      <label key={option.value} className="visa-option-card">
                        <input
                          type="checkbox"
                          onChange={(e) => handleMultipleChoice(`guarantor_${guarantor}_status`, option.value, e.target.checked)}
                          className="visa-checkbox"
                        />
                        <div className="visa-card-content">
                          <div className="visa-card-icon">👔</div>
                          <div>
                            <h3 className="visa-card-title">{option.label}</h3>
                            <p className="visa-card-description">Indiquez le statut du garant.</p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('funds-proof')}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'funds-proof' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Preuve de fonds pour chaque garant</h2>
              <p className="visa-step-subtitle">Indiquez la preuve de fonds pour chaque garant (épargne, vente de bien, etc.).</p>
            </div>
            
            <div className="visa-grid">
              {responses.guarantors && responses.guarantors.map(guarantor => (
                <div key={guarantor} className="visa-option-card">
                  <h3 className="visa-card-title">Preuve de fonds - {getGuarantorLabel(guarantor)}</h3>
                  <div className="visa-grid">
                    {[
                      { value: 'epargne', label: 'Épargne' },
                      { value: 'vente_bien', label: 'Vente de bien' },
                      { value: 'heritage', label: 'Héritage' },
                      { value: 'credit_etude', label: 'Crédit étude' }
                    ].map(option => (
                      <label key={option.value} className="visa-option-card">
                        <input
                          type="checkbox"
                          onChange={(e) => handleMultipleChoice(`guarantor_${guarantor}_funds`, option.value, e.target.checked)}
                          className="visa-checkbox"
                        />
                        <div className="visa-card-content">
                          <div className="visa-card-icon">💰</div>
                          <div>
                            <h3 className="visa-card-title">{option.label}</h3>
                            <p className="visa-card-description">Indiquez la preuve des fonds du garant.</p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('property-question')}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'property-question' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Propriétés des garants</h2>
              <p className="visa-step-subtitle">Indiquez si les garants possèdent des biens immobiliers.</p>
            </div>
            
            <div className="visa-grid">
              {responses.guarantors && responses.guarantors.map(guarantor => (
                <div key={guarantor} className="visa-option-card">
                  <h3 className="visa-card-title">{getGuarantorLabel(guarantor)} - Possède-t-il/elle des biens ?</h3>
                  <div className="visa-grid">
                    {[
                      { value: 'oui', label: 'Oui' },
                      { value: 'non', label: 'Non' }
                    ].map(option => (
                      <label key={option.value} className="visa-option-card">
                        <input
                          type="radio"
                          name={`property_${guarantor}`}
                          value={option.value}
                          onChange={(e) => handleResponse(`guarantor_${guarantor}_property`, e.target.value)}
                          className="visa-radio"
                        />
                        <div className="visa-card-content">
                          <div className="visa-card-icon">🏠</div>
                          <div>
                            <h3 className="visa-card-title">{option.label}</h3>
                            <p className="visa-card-description">Indiquez si le garant possède des biens.</p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => {
                  if (responses.accompaniedBy && responses.accompaniedBy.includes('spouse')) {
                    nextStep('spouse-residence');
                  } else if (responses.accompaniedBy && responses.accompaniedBy.includes('children')) {
                    nextStep('children-questions');
                  } else {
                    nextStep('additional-docs');
                  }
                }}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'spouse-residence' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">L'époux/épouse a-t-il/elle déjà résidé plus de 6 mois dans un autre pays ?</h2>
              <p className="visa-step-subtitle">Indiquez si l'époux/épouse a résidé plus de 6 mois dans un autre pays.</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: 'oui', label: 'Oui' },
                { value: 'non', label: 'Non' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="radio"
                    name="spouseResidence"
                    value={option.value}
                    onChange={(e) => handleResponse('spouseResidedAbroad', e.target.value)}
                    className="visa-radio"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">🌍</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez si l'époux/épouse a résidé plus de 6 mois dans un autre pays.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('spouse-property')}
                disabled={!responses.spouseResidedAbroad}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'spouse-property' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">L'époux/épouse possède-t-il/elle des biens ?</h2>
              <p className="visa-step-subtitle">Indiquez si l'époux/épouse possède des biens immobiliers.</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: 'oui', label: 'Oui' },
                { value: 'non', label: 'Non' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="radio"
                    name="spouseProperty"
                    value={option.value}
                    onChange={(e) => handleResponse('spouseHasProperty', e.target.value)}
                    className="visa-radio"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">🏠</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez si l'époux/épouse possède des biens.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('spouse-status')}
                disabled={!responses.spouseHasProperty}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'spouse-status' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Statut de l'époux/épouse</h2>
              <p className="visa-step-subtitle">Indiquez le statut de l'époux/épouse (salarié, étudiant, etc.).</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: 'salarie', label: 'Salarié' },
                { value: 'etudiant', label: 'Étudiant' },
                { value: 'entrepreneur_avec_salaire', label: 'Entrepreneur avec salaire' },
                { value: 'entrepreneur_sans_salaire', label: 'Entrepreneur sans salaire' },
                { value: 'retraite', label: 'Retraité' },
                { value: 'heritage', label: 'Héritage' },
                { value: 'femme_foyer', label: 'Femme au foyer' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="checkbox"
                    onChange={(e) => handleMultipleChoice('spouseStatus', option.value, e.target.checked)}
                    className="visa-checkbox"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">👔</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez le statut de l'époux/épouse.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('spouse-funds')}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'spouse-funds' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">L'époux/épouse dispose-t-il/elle de fonds ?</h2>
              <p className="visa-step-subtitle">Indiquez si l'époux/épouse possède des fonds propres.</p>
            </div>
            
            <div className="visa-grid">
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="spouseHasFunds"
                  value="oui"
                  onChange={(e) => handleResponse('spouseHasFunds', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">💰</div>
                  <div>
                    <h3 className="visa-card-title">Oui</h3>
                    <p className="visa-card-description">Indiquez si l'époux/épouse possède des fonds.</p>
                  </div>
                </div>
              </label>
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="spouseHasFunds"
                  value="non"
                  onChange={(e) => handleResponse('spouseHasFunds', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">💰</div>
                  <div>
                    <h3 className="visa-card-title">Non</h3>
                    <p className="visa-card-description">Indiquez si l'époux/épouse possède des fonds.</p>
                  </div>
                </div>
              </label>
            </div>

            {responses.spouseHasFunds === 'oui' && (
              <div className="visa-step-actions">
                <h3 className="visa-card-title">Quelle est la preuve des fonds ? (Plusieurs choix possibles)</h3>
                <div className="visa-grid">
                  {[
                    { value: 'epargne', label: 'Épargne' },
                    { value: 'vente_bien', label: 'Vente de bien' },
                    { value: 'heritage', label: 'Héritage' },
                    { value: 'credit_etude', label: 'Crédit étude' }
                  ].map(option => (
                    <label key={option.value} className="visa-option-card">
                      <input
                        type="checkbox"
                        onChange={(e) => handleMultipleChoice('spouseFunds', option.value, e.target.checked)}
                        className="visa-checkbox"
                      />
                      <div className="visa-card-content">
                        <div className="visa-card-icon">💰</div>
                        <div>
                          <h3 className="visa-card-title">{option.label}</h3>
                          <p className="visa-card-description">Indiquez la preuve des fonds de l'époux/épouse.</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="visa-step-actions">
              <button
                onClick={() => {
                  if (responses.accompaniedBy && responses.accompaniedBy.includes('children')) {
                    nextStep('children-questions');
                  } else {
                    nextStep('additional-docs');
                  }
                }}
                disabled={!responses.spouseHasFunds}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'children-questions' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Questions concernant les enfants</h2>
              <p className="visa-step-subtitle">L'étudiant voyage-t-il avec un seul parent ?</p>
            </div>
            
            <div className="visa-grid">
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="singleParent"
                  value="oui"
                  onChange={(e) => handleResponse('singleParentTravel', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">👶</div>
                  <div>
                    <h3 className="visa-card-title">Oui, avec un seul parent</h3>
                    <p className="visa-card-description">Indiquez si l'étudiant voyage avec un seul parent.</p>
                  </div>
                </div>
              </label>
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="singleParent"
                  value="non"
                  onChange={(e) => handleResponse('singleParentTravel', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">👨‍👩‍👧‍👦</div>
                  <div>
                    <h3 className="visa-card-title">Non, avec les deux parents</h3>
                    <p className="visa-card-description">Indiquez si l'étudiant voyage avec les deux parents.</p>
                  </div>
                </div>
              </label>
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('additional-docs')}
                disabled={!responses.singleParentTravel}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'additional-docs' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Documents complémentaires (optionnel)</h2>
              <p className="visa-step-subtitle">Ajoutez des documents complémentaires si nécessaire.</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: 'frais_scolarite', label: 'Preuve de paiement des frais de scolarité (total ou partiel)' },
                { value: 'certificat_garantie', label: 'Certificat de garantie au Canada (pour les frais de subsistance de la première année d\'études)' },
                { value: 'invitation_bail', label: 'Lettre d\'invitation ou contrat de bail' },
                { value: 'promesse_embauche', label: 'Promesse d\'embauche hors Canada après l\'obtention du diplôme' },
                { value: 'reussites', label: 'Toute autre preuve de réussite de l\'étudiant (personnelle, associative, professionnelle ou scolaire)' },
                { value: 'attestation_blocage', label: 'Attestation de blocage des fonds pour les frais de subsistance de la première année' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="checkbox"
                    onChange={(e) => handleMultipleChoice('additionalDocs', option.value, e.target.checked)}
                    className="visa-checkbox"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">📄</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez les documents complémentaires que vous souhaitez ajouter.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={generateDocumentList}
                className="visa-button-primary"
              >
                <span className="button-icon">✓</span>
                Générer la liste de documents
              </button>
            </div>
          </div>
        )}

        {/* --- VISITOR FLOW --- */}

        {currentStep === 'visitor-marital-status' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Quelle est votre situation matrimoniale ?</h2>
              <p className="visa-step-subtitle">Votre état matrimonial est pris en compte même si votre conjoint ne voyage pas.</p>
            </div>
            <div className="visa-grid">
              {[
                { value: 'single', label: 'Célibataire' },
                { value: 'married', label: 'Marié(e)' },
                { value: 'common-law', label: 'Conjoint(e) de fait' },
                { value: 'separated', label: 'Séparé(e), toujours marié(e)' },
                { value: 'divorced', label: 'Divorcé(e)' },
                { value: 'widowed', label: 'Veuf / veuve' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input type="radio" name="visitorMaritalStatus" value={option.value}
                    checked={responses.visitorMaritalStatus === option.value}
                    onChange={() => setResponses(previous => ({
                      ...previous, visitorMaritalStatus: option.value as Responses['visitorMaritalStatus'],
                      visitorSpouseAccompanying: undefined, visitorVisaType: undefined,
                      visitorFamilyMembers: (previous.visitorFamilyMembers || []).filter(member => member !== 'spouse')
                    }))} className="visa-radio" />
                  <div className="visa-card-content"><h3 className="visa-card-title">{option.label}</h3></div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep(visitorHasPartner(responses) ? 'visitor-spouse-accompanying' : 'visitor-type')}
                disabled={!responses.visitorMaritalStatus} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-spouse-accompanying' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Votre époux/épouse ou conjoint de fait vous accompagne-t-il ?</h2>
              <p className="visa-step-subtitle">L'acte de mariage ou les preuves d'union restent à préparer même en cas de voyage sans votre conjoint.</p>
            </div>
            <div className="visa-grid">
              {[
                { value: 'oui', label: 'Oui, mon conjoint voyage avec moi' },
                { value: 'non', label: 'Non, mon conjoint ne voyage pas' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input type="radio" name="visitorSpouseAccompanying" value={option.value}
                    checked={responses.visitorSpouseAccompanying === option.value}
                    onChange={() => setResponses(previous => ({
                      ...previous, visitorSpouseAccompanying: option.value as 'oui' | 'non',
                      visitorVisaType: option.value === 'oui' ? 'famille' : undefined,
                      visitorFamilyMembers: option.value === 'oui'
                        ? Array.from(new Set(['principal', 'spouse', ...(previous.visitorFamilyMembers || [])]))
                        : (previous.visitorFamilyMembers || []).filter(member => member !== 'spouse')
                    }))} className="visa-radio" />
                  <div className="visa-card-content"><h3 className="visa-card-title">{option.label}</h3></div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep(responses.visitorSpouseAccompanying === 'oui' ? 'visitor-family-members' : 'visitor-type')}
                disabled={!responses.visitorSpouseAccompanying} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-type' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Type de visa visiteur</h2>
              <p className="visa-step-subtitle">Est-ce une demande individuelle ou pour une famille ?</p>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="visitorVisaType"
                  value="individuel"
                  checked={responses.visitorVisaType === 'individuel'}
                  onChange={() => setResponses(previous => ({ ...previous, visitorVisaType: 'individuel', visitorFamilyMembers: [] }))}
                  className="visa-radio"
                />
                <div className="visa-card-content-vertical">
                  <div className="visa-card-icon-large">👤</div>
                  <h3 className="visa-card-title">Individuel</h3>
                </div>
              </label>
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="visitorVisaType"
                  value="famille"
                  checked={responses.visitorVisaType === 'famille'}
                  onChange={(e) => handleResponse('visitorVisaType', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content-vertical">
                  <div className="visa-card-icon-large">👨‍👩‍👧‍👦</div>
                  <h3 className="visa-card-title">Famille</h3>
                </div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button
                onClick={() => nextStep(responses.visitorVisaType === 'famille' ? 'visitor-family-members' : 'visitor-travel-guarantors')}
                disabled={!responses.visitorVisaType}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-travel-guarantors' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Qui est le garant pour le voyage ?</h2>
              <p className="visa-step-subtitle">Vous pouvez choisir plusieurs réponses.</p>
            </div>
            <div className="visa-grid">
              {[
                { value: 'Lui-même', label: 'Lui-même' },
                { value: 'Le mari', label: 'Le mari' },
                { value: "L'épouse", label: "L'épouse" },
                { value: 'Le père', label: 'Le père' },
                { value: 'La mère', label: 'La mère' },
                { value: 'Le frère', label: 'Le frère' },
                { value: 'La sœur', label: 'La sœur' },
                { value: 'Le grand-père', label: 'Le grand-père' },
                { value: 'La grand-mère', label: 'La grand-mère' },
                { value: 'Le fils', label: 'Le fils' },
                { value: 'La fille', label: 'La fille' },
                { value: 'Autre', label: 'Autre' },
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="checkbox"
                    checked={((responses as any).visitorTravelGuarantors || []).includes(option.value)}
                    onChange={(e) => handleMultipleChoice('visitorTravelGuarantors', option.value, e.target.checked)}
                    className="visa-checkbox"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">🤝</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Sélectionnez les garants du voyage.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('visitor-principal-property')}
                disabled={!(responses as any).visitorTravelGuarantors || (responses as any).visitorTravelGuarantors.length === 0}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-family-members' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Qui fait partie du voyage ?</h2>
              <p className="visa-step-subtitle">Sélectionnez tous les membres de la famille concernés.</p>
            </div>
            <div className="visa-grid">
              {[
                { value: 'principal', label: 'Requérant Principal' },
                { value: 'spouse', label: 'Époux/Épouse' },
                { value: 'children', label: 'Enfants' },
              ].filter(option => option.value !== 'spouse' || responses.visitorSpouseAccompanying === 'oui').map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="checkbox"
                    checked={(responses.visitorFamilyMembers || []).includes(option.value)}
                    disabled={option.value === 'spouse' && responses.visitorSpouseAccompanying === 'oui'}
                    onChange={(e) => handleMultipleChoice('visitorFamilyMembers', option.value, e.target.checked)}
                    className="visa-checkbox"
                  />
                  <div className="visa-card-content">
                    <h3 className="visa-card-title">{option.label}</h3>
                  </div>
                </label>
              ))}
            </div>
            {responses.visitorFamilyMembers?.includes('children') && (
              <div style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                borderRadius: '16px',
                border: '2px solid #90caf9'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #2196f3 0%, #9c27b0 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '1rem',
                    fontSize: '24px',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                  }}>
                    👶
                  </div>
                  <div>
                    <label style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#1976d2',
                      marginBottom: '4px',
                      display: 'block'
                    }}>
                      Nombre d'enfants
                    </label>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#5e35b1',
                      margin: 0
                    }}>
                      Précisez combien d'enfants accompagneront le voyageur
                    </p>
                  </div>
                </div>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={responses.visitorChildrenCount || ''}
                  onChange={(e) => handleResponse('visitorChildrenCount', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    border: '2px solid #90caf9',
                    borderRadius: '12px',
                    background: 'white',
                    color: '#1976d2',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
                  }}
                  placeholder="Entrez"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2196f3';
                    e.target.style.boxShadow = '0 4px 16px rgba(33, 150, 243, 0.3)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#90caf9';
                    e.target.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.1)';
                  }}
                />
              </div>
            )}
            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('visitor-principal-property')}
                disabled={!responses.visitorFamilyMembers || responses.visitorFamilyMembers.length === 0}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-principal-property' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Le requérant principal possède-t-il des biens ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="visitorPrincipalProperty" value="oui" onChange={(e) => handleResponse('visitorPrincipal.hasProperty', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="visitorPrincipalProperty" value="non" onChange={(e) => handleResponse('visitorPrincipal.hasProperty', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('visitor-principal-status')} disabled={!responses.visitorPrincipal?.hasProperty} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-principal-status' && (
          <div className="visa-step-container">
            <div className="visa-step-header"><h2 className="visa-step-title">Quel est le statut du requérant principal ?</h2></div>
            <div className="visa-grid">
              {[
                { value: 'salarie', label: 'Salarié' },
                { value: 'entrepreneur_avec_salaire', label: 'Entrepreneur avec salaire' },
                { value: 'entrepreneur_sans_salaire', label: 'Entrepreneur sans salaire' },
                { value: 'agriculteur', label: 'Agriculteur' },
                { value: 'proprietaire_location', label: 'Propriétaire de bien en location' },
                { value: 'retraite', label: 'Retraité' },
                { value: 'etudiant', label: 'Étudiant' },
                { value: 'femme_foyer', label: 'Femme au foyer (sans emploi)' },
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input type="checkbox" onChange={(e) => handleMultipleChoice('visitorPrincipal.status', option.value, e.target.checked)} className="visa-checkbox" />
                  <div className="visa-card-content"><h3 className="visa-card-title">{option.label}</h3></div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('visitor-principal-funds')} disabled={!responses.visitorPrincipal?.status?.length} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-principal-funds' && (
          <div className="visa-step-container">
            <div className="visa-step-header"><h2 className="visa-step-title">Quelle est la preuve de fonds pour le requérant principal ?</h2></div>
            <div className="visa-grid">
               {[
                { value: 'epargne', label: 'Épargne' },
                { value: 'vente_bien', label: 'Vente de bien' },
                { value: 'heritage', label: 'Héritage' },
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input type="checkbox" onChange={(e) => handleMultipleChoice('visitorPrincipal.funds', option.value, e.target.checked)} className="visa-checkbox" />
                  <div className="visa-card-content"><h3 className="visa-card-title">{option.label}</h3></div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => {
                 const visitorTravelGuarantors: string[] = (responses as any).visitorTravelGuarantors || [];
                 const otherGuarantors = visitorTravelGuarantors.filter(g => g !== 'Lui-même');

                 if ((responses as any).visitorVisaType === 'individuel' && otherGuarantors.length > 0) {
                  nextStep('visitor-guarantors-property');
                } else if (responses.visitorFamilyMembers?.includes('spouse')) {
                  nextStep('visitor-spouse-property');
                } else if (responses.visitorFamilyMembers?.includes('children')) {
                  nextStep('visitor-children-single-parent');
                } else {
                  nextStep('visitor-canada-invitation');
                }
              }}  className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-guarantors-property' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Les garants possèdent-ils des biens ?</h2>
              <p className="visa-step-subtitle">Répondez pour chaque garant sélectionné.</p>
            </div>

            <div className="visa-grid">
              {(((responses as any).visitorTravelGuarantors || []) as string[])
                .filter(g => g !== 'Lui-même')
                .map((g) => (
                  <div key={g} className="visa-option-card" style={{ padding: 16 }}>
                    <h3 className="visa-card-title" style={{ marginBottom: 8 }}>{g}</h3>
                    <div className="visa-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                      <label className="visa-option-card">
                        <input
                          type="radio"
                          name={`visitorGuarantorBiens_${g}`}
                          value="oui"
                          onChange={(e) => {
                            const currentMap = (responses as any).visitorGuarantorsBiens || {};
                            handleResponse('visitorGuarantorsBiens', { ...currentMap, [g]: e.target.value });
                          }}
                          className="visa-radio"
                        />
                        <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
                      </label>
                      <label className="visa-option-card">
                        <input
                          type="radio"
                          name={`visitorGuarantorBiens_${g}`}
                          value="non"
                          onChange={(e) => {
                            const currentMap = (responses as any).visitorGuarantorsBiens || {};
                            handleResponse('visitorGuarantorsBiens', { ...currentMap, [g]: e.target.value });
                          }}
                          className="visa-radio"
                        />
                        <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
                      </label>
                    </div>
                  </div>
                ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('visitor-guarantors-status')}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-guarantors-status' && (
          <div className="visa-step-container">
            <div className="visa-step-header"><h2 className="visa-step-title">Quel est le statut de chaque garant ?</h2></div>
            <div className="visa-grid">
              {(((responses as any).visitorTravelGuarantors || []) as string[])
                .filter(g => g !== 'Lui-même')
                .map((g) => (
                  <div key={g} className="visa-option-card" style={{ padding: 16 }}>
                    <h3 className="visa-card-title" style={{ marginBottom: 8 }}>{g}</h3>
                    <div className="visa-grid">
                      {[
                        { value: 'salarie', label: 'Salarié' },
                        { value: 'entrepreneur_avec_salaire', label: 'Entrepreneur avec salaire' },
                        { value: 'entrepreneur_sans_salaire', label: 'Entrepreneur sans salaire' },
                        { value: 'agriculteur', label: 'Agriculteur' },
                        { value: 'proprietaire_location', label: 'Propriétaire de bien en location' },
                        { value: 'retraite', label: 'Retraité' },
                        { value: 'etudiant', label: 'Étudiant' },
                        { value: 'femme_foyer', label: 'Femme au foyer (sans emploi)' },
                      ].map(option => (
                        <label key={`${g}_${option.value}`} className="visa-option-card">
                          <input
                            type="checkbox"
                            onChange={(e) => handleMultipleChoiceInMap('visitorGuarantorsStatus', g, option.value, e.target.checked)}
                            className="visa-checkbox"
                          />
                          <div className="visa-card-content"><h3 className="visa-card-title">{option.label}</h3></div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('visitor-guarantors-funds')} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-guarantors-funds' && (
          <div className="visa-step-container">
            <div className="visa-step-header"><h2 className="visa-step-title">Quelle est la preuve de fonds pour chaque garant ?</h2></div>
            <div className="visa-grid">
              {(((responses as any).visitorTravelGuarantors || []) as string[])
                .filter(g => g !== 'Lui-même')
                .map((g) => (
                  <div key={g} className="visa-option-card" style={{ padding: 16 }}>
                    <h3 className="visa-card-title" style={{ marginBottom: 8 }}>{g}</h3>
                    <div className="visa-grid">
                      {[
                        { value: 'epargne', label: 'Épargne' },
                        { value: 'vente_bien', label: 'Vente de bien' },
                        { value: 'heritage', label: 'Héritage' },
                      ].map(option => (
                        <label key={`${g}_${option.value}`} className="visa-option-card">
                          <input
                            type="checkbox"
                            onChange={(e) => handleMultipleChoiceInMap('visitorGuarantorsFunds', g, option.value, e.target.checked)}
                            className="visa-checkbox"
                          />
                          <div className="visa-card-content"><h3 className="visa-card-title">{option.label}</h3></div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('visitor-canada-invitation')} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-canada-invitation' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Avez-vous quelqu'un au Canada qui va vous inviter ?</h2>
              <p className="visa-step-subtitle">Une invitation n'est pas nécessaire pour tout voyage touristique. Si vous avez un hôte, précisez son lien avec vous, son statut et son soutien.</p>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="visitorHasCanadaInvitation"
                  value="oui"
                  checked={responses.visitorHasCanadaInvitation === 'oui'}
                  onChange={(e) => handleResponse('visitorHasCanadaInvitation', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content-vertical">
                  <div className="visa-card-icon-large">✅</div>
                  <h3 className="visa-card-title">Oui</h3>
                  <p className="visa-card-description">J'ai quelqu'un au Canada qui va m'inviter</p>
                </div>
              </label>
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="visitorHasCanadaInvitation"
                  value="non"
                  checked={responses.visitorHasCanadaInvitation === 'non'}
                  onChange={(e) => handleResponse('visitorHasCanadaInvitation', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content-vertical">
                  <div className="visa-card-icon-large">❌</div>
                  <h3 className="visa-card-title">Non</h3>
                  <p className="visa-card-description">Je n'ai personne au Canada</p>
                </div>
              </label>
            </div>
            {responses.visitorHasCanadaInvitation === 'oui' && (
              <div className="visa-invitation-details">
                <div className="visa-form-group">
                  <label htmlFor="visitor-inviter-relationship">Quel est votre lien avec la personne qui vous invite ?</label>
                  <select id="visitor-inviter-relationship" className="visa-select" value={responses.visitorInviterRelationship || ''}
                    onChange={event => handleResponse('visitorInviterRelationship', event.target.value)}>
                    <option value="">Sélectionner le lien</option>
                    {VISITOR_RELATIONSHIPS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                {responses.visitorInviterRelationship === 'other' && (
                  <div className="visa-form-group">
                    <label htmlFor="visitor-inviter-relationship-other">Précisez votre lien avec cette personne</label>
                    <input id="visitor-inviter-relationship-other" className="visa-input" type="text" maxLength={150}
                      value={responses.visitorInviterRelationshipOther || ''} onChange={event => handleResponse('visitorInviterRelationshipOther', event.target.value)} />
                  </div>
                )}
                <div className="visa-form-group">
                  <label htmlFor="visitor-inviter-status">Quel est son statut au Canada ?</label>
                  <select id="visitor-inviter-status" className="visa-select" value={responses.visitorInviterStatus || ''}
                    onChange={event => handleResponse('visitorInviterStatus', event.target.value)}>
                    <option value="">Sélectionner le statut</option>
                    {VISITOR_INVITER_STATUSES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                {responses.visitorInviterStatus === 'other' && (
                  <div className="visa-form-group">
                    <label htmlFor="visitor-inviter-status-other">Précisez son statut actuel au Canada</label>
                    <input id="visitor-inviter-status-other" className="visa-input" type="text" maxLength={150}
                      value={responses.visitorInviterStatusOther || ''} onChange={event => handleResponse('visitorInviterStatusOther', event.target.value)} />
                  </div>
                )}
                <fieldset className="visa-form-group">
                  <legend>Cette personne vous héberge-t-elle ?</legend>
                  {['oui', 'non'].map(value => (
                    <label key={value} className="visa-option-card">
                      <input type="radio" name="visitorInviterHosts" value={value} checked={responses.visitorInviterHosts === value}
                        onChange={event => handleResponse('visitorInviterHosts', event.target.value)} className="visa-radio" />
                      <span>{value === 'oui' ? "Oui, je loge chez l'hôte" : 'Non, je prévois un autre hébergement'}</span>
                    </label>
                  ))}
                </fieldset>
                <fieldset className="visa-form-group">
                  <legend>Cette personne finance-t-elle tout ou partie de votre séjour ?</legend>
                  {['oui', 'non'].map(value => (
                    <label key={value} className="visa-option-card">
                      <input type="radio" name="visitorInviterPays" value={value} checked={responses.visitorInviterPays === value}
                        onChange={event => handleResponse('visitorInviterPays', event.target.value)} className="visa-radio" />
                      <span>{value === 'oui' ? 'Oui, elle participe aux dépenses' : 'Non, elle ne finance pas le séjour'}</span>
                    </label>
                  ))}
                </fieldset>
              </div>
            )}
            <div className="visa-step-actions">
              <button
                onClick={() => generateDocumentList()}
                disabled={!isVisitorInvitationComplete(responses)}
                className="visa-button-primary"
              >
                Générer la liste de documents
              </button>
            </div>
          </div>
        )}
        
        {currentStep === 'visitor-guarantor-property' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Le garant possède-t-il des biens ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="visitorGuarantorProperty" value="oui" onChange={(e) => handleResponse('visitorGuarantor.hasProperty', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="visitorGuarantorProperty" value="non" onChange={(e) => handleResponse('visitorGuarantor.hasProperty', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('visitor-guarantor-status')} disabled={!responses.visitorGuarantor?.hasProperty} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-guarantor-status' && (
          <div className="visa-step-container">
            <div className="visa-step-header"><h2 className="visa-step-title">Quel est le statut du garant ?</h2></div>
            <div className="visa-grid">
              {[
                { value: 'salarie', label: 'Salarié' },
                { value: 'entrepreneur_avec_salaire', label: 'Entrepreneur avec salaire' },
                { value: 'entrepreneur_sans_salaire', label: 'Entrepreneur sans salaire' },
                { value: 'agriculteur', label: 'Agriculteur' },
                { value: 'proprietaire_location', label: 'Propriétaire de bien en location' },
                { value: 'retraite', label: 'Retraité' },
                { value: 'etudiant', label: 'Étudiant' },
                { value: 'femme_foyer', label: 'Femme au foyer (sans emploi)' },
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input type="checkbox" onChange={(e) => handleMultipleChoice('visitorGuarantor.status', option.value, e.target.checked)} className="visa-checkbox" />
                  <div className="visa-card-content"><h3 className="visa-card-title">{option.label}</h3></div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('visitor-guarantor-funds')} disabled={!responses.visitorGuarantor?.status?.length} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-guarantor-funds' && (
          <div className="visa-step-container">
            <div className="visa-step-header"><h2 className="visa-step-title">Quelle est la preuve de fonds pour le garant ?</h2></div>
            <div className="visa-grid">
               {[
                { value: 'epargne', label: 'Épargne' },
                { value: 'vente_bien', label: 'Vente de bien' },
                { value: 'heritage', label: 'Héritage' },
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input type="checkbox" onChange={(e) => handleMultipleChoice('visitorGuarantor.funds', option.value, e.target.checked)} className="visa-checkbox" />
                  <div className="visa-card-content"><h3 className="visa-card-title">{option.label}</h3></div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => {
                 if (responses.visitorFamilyMembers?.includes('children')) {
                  nextStep('visitor-children-single-parent');
                } else {
                  nextStep('visitor-canada-invitation');
                }
              }}  className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-spouse-property' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">L'époux/épouse possède-t-il/elle des biens ?</h2>
              <p className="visa-step-subtitle">Indiquez si l'époux/épouse possède des biens immobiliers.</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: 'oui', label: 'Oui' },
                { value: 'non', label: 'Non' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="radio"
                    name="visitorSpouseProperty"
                    value={option.value}
                    onChange={(e) => handleResponse('visitorSpouse.hasProperty', e.target.value)}
                    className="visa-radio"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">🏠</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez si l'époux/épouse possède des biens.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('visitor-spouse-status')}
                disabled={!responses.visitorSpouse?.hasProperty}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-spouse-status' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Statut de l'époux/épouse</h2>
              <p className="visa-step-subtitle">Indiquez le statut de l'époux/épouse (salarié, étudiant, etc.).</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: 'salarie', label: 'Salarié' },
                { value: 'etudiant', label: 'Étudiant' },
                { value: 'entrepreneur_avec_salaire', label: 'Entrepreneur avec salaire' },
                { value: 'entrepreneur_sans_salaire', label: 'Entrepreneur sans salaire' },
                { value: 'retraite', label: 'Retraité' },
                { value: 'heritage', label: 'Héritage' },
                { value: 'femme_foyer', label: 'Femme au foyer' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="checkbox"
                    onChange={(e) => handleMultipleChoice('visitorSpouse.status', option.value, e.target.checked)}
                    className="visa-checkbox"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">👔</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez le statut de l'époux/épouse.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('visitor-spouse-funds')}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-spouse-funds' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">L'époux/épouse dispose-t-il/elle de fonds ?</h2>
              <p className="visa-step-subtitle">Indiquez si l'époux/épouse possède des fonds propres.</p>
            </div>
            
            <div className="visa-grid">
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="visitorSpouseHasFunds"
                  value="oui"
                  onChange={(e) => handleResponse('visitorSpouse.hasFunds', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">💰</div>
                  <div>
                    <h3 className="visa-card-title">Oui</h3>
                    <p className="visa-card-description">Indiquez si l'époux/épouse possède des fonds.</p>
                  </div>
                </div>
              </label>
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="visitorSpouseHasFunds"
                  value="non"
                  onChange={(e) => handleResponse('visitorSpouse.hasFunds', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">💰</div>
                  <div>
                    <h3 className="visa-card-title">Non</h3>
                    <p className="visa-card-description">Indiquez si l'époux/épouse possède des fonds.</p>
                  </div>
                </div>
              </label>
            </div>

            {responses.visitorSpouse?.hasFunds === 'oui' && (
              <div className="visa-step-actions">
                <h3 className="visa-card-title">Quelle est la preuve des fonds ? (Plusieurs choix possibles)</h3>
                <div className="visa-grid">
                  {[
                    { value: 'epargne', label: 'Épargne' },
                    { value: 'vente_bien', label: 'Vente de bien' },
                    { value: 'heritage', label: 'Héritage' },
                    { value: 'credit_etude', label: 'Crédit étude' }
                  ].map(option => (
                    <label key={option.value} className="visa-option-card">
                      <input
                        type="checkbox"
                        onChange={(e) => handleMultipleChoice('visitorSpouse.funds', option.value, e.target.checked)}
                        className="visa-checkbox"
                      />
                      <div className="visa-card-content">
                        <div className="visa-card-icon">💰</div>
                        <div>
                          <h3 className="visa-card-title">{option.label}</h3>
                          <p className="visa-card-description">Indiquez la preuve des fonds de l'époux/épouse.</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="visa-step-actions">
              <button
                onClick={() => {
                  if (responses.visitorFamilyMembers?.includes('children')) {
                    nextStep('visitor-children-single-parent');
                  } else {
                    nextStep('visitor-canada-invitation');
                  }
                }}
                disabled={!responses.visitorSpouse?.hasFunds}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'visitor-children-single-parent' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">L'étudiant voyage-t-il avec un seul parent ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="visitorChildrenSingleParent"
                  value="oui"
                  onChange={(e) => handleResponse('visitorSingleParent', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">👶</div>
                  <div>
                    <h3 className="visa-card-title">Oui, avec un seul parent</h3>
                    <p className="visa-card-description">Indiquez si l'étudiant voyage avec un seul parent.</p>
                  </div>
                </div>
              </label>
              <label className="visa-option-card">
                <input
                  type="radio"
                  name="visitorChildrenSingleParent"
                  value="non"
                  onChange={(e) => handleResponse('visitorSingleParent', e.target.value)}
                  className="visa-radio"
                />
                <div className="visa-card-content">
                  <div className="visa-card-icon">👨‍👩‍👧‍👦</div>
                  <div>
                    <h3 className="visa-card-title">Non, avec les deux parents</h3>
                    <p className="visa-card-description">Indiquez si l'étudiant voyage avec les deux parents.</p>
                  </div>
                </div>
              </label>
            </div>

            <div className="visa-step-actions">
              <button
                onClick={() => nextStep('visitor-canada-invitation')}
                disabled={!responses.visitorSingleParent}
                className="visa-button-primary"
              >
                Continuer <span className="button-arrow">→</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'final' && (
          <div className="visa-step-container bounce-in">
            <div className="visa-step-header">
              <div className="visa-success-icon">
                <span>✓</span>
              </div>
              <h2 className="visa-step-title">Votre liste de documents est prête !</h2>
              <p className="visa-step-subtitle">Voici tous les documents nécessaires pour votre demande de visa.</p>
            </div>
            
            <div className="document-list-container">
              <div className="document-list">
                {documentList.map((doc, index) => {
                  const isMainHeader = doc.startsWith('===');
                  const isSubHeader = doc.startsWith('---');
                  const isDocument = !isMainHeader && !isSubHeader;

                  return (
                    <div key={index} className={`document-item ${
                      isMainHeader ? 'doc-main-header' :
                      isSubHeader ? 'doc-sub-header' :
                      'doc-bullet-item'
                    }`}>
                      {isMainHeader && <span className="doc-icon">📋</span>}
                      {isSubHeader && <span className="doc-icon">📄</span>}
                      {isDocument && <span className="doc-bullet">—</span>}
                      <span className="doc-text">
                        {doc
                          .replace(/^===\s*/, '')
                          .replace(/\s*===$/, '')
                          .replace(/^---\s*/, '')
                          .replace(/\s*---$/, '')
                          .replace(/^(?:•|-|–|—)\s*/, '')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="visa-step-actions-final">
              <button
                onClick={resetForm}
                className="visa-button-secondary"
              >
                <span className="button-icon">🔄</span>
                Nouvelle recherche
              </button>
              <button
                onClick={downloadList}
                className="visa-button-primary"
              >
                <span className="button-icon">⬇</span>
                Télécharger la liste
              </button>
            </div>
          </div>
        )}

        {/* --- ENTRÉE EXPRESS FLOW --- */}

        {currentStep === 'qc-program' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Quelle voie d'immigration permanente au Québec s'applique ?</h2>
              <p className="visa-step-subtitle">Le PSTQ et le PEQ ont des règles et des documents différents. Ce questionnaire ne remplace pas une décision du MIFI.</p>
            </div>
            <div className="visa-grid">
              {[
                { value: 'pstq', label: 'PSTQ', desc: 'Programme de sélection des travailleurs qualifiés, avec déclaration d’intérêt et invitation Arrima.' },
                { value: 'peq-travailleur', label: 'PEQ – Travailleur temporaire', desc: 'Pour une personne qui travaille légalement au Québec et satisfait aux conditions du PEQ.' },
                { value: 'peq-diplome', label: 'PEQ – Diplômé du Québec', desc: 'Pour une personne diplômée du Québec répondant aux conditions de réception du PEQ.' },
                { value: 'autre', label: 'Autre programme', desc: 'Gens d’affaires, regroupement familial ou autre situation : validation nécessaire avant le dépôt.' }
              ].map((option) => (
                <label key={option.value} className="visa-option-card">
                  <input type="radio" name="qcProgram" value={option.value} checked={responses.quebecProgram === option.value} onChange={(e) => handleResponse('quebecProgram', e.target.value)} className="visa-radio" />
                  <div className="visa-card-content"><div><h3 className="visa-card-title">{option.label}</h3><p className="visa-card-description">{option.desc}</p></div></div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep(responses.quebecProgram === 'pstq' ? 'qc-pstq-invited' : responses.quebecProgram === 'peq-travailleur' ? 'qc-peq-worker-experience' : responses.quebecProgram === 'peq-diplome' ? 'qc-peq-graduate-credential' : 'qc-family')} disabled={!responses.quebecProgram} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {renderEEChoiceStep(
          'qc-pstq-invited',
          'Avez-vous déjà reçu une invitation PSTQ dans Arrima ?',
          'qcPstqInvited',
          [{ value: 'oui', label: 'Oui', description: 'La demande de sélection permanente et les pièces à l’appui seront préparées.' }, { value: 'non', label: 'Non', description: 'Préparation de la déclaration d’intérêt uniquement; aucune demande de sélection n’est déposée sans invitation.' }],
          'qc-pstq-stream'
        )}

        {renderEEChoiceStep(
          'qc-pstq-stream',
          'Quel volet PSTQ est visé ou le plus probable ?',
          'qcPstqStream',
          [
            { value: '1', label: 'Volet 1 – Haute qualification et compétences spécialisées' },
            { value: '2', label: 'Volet 2 – Compétences intermédiaires et manuelles' },
            { value: '3', label: 'Volet 3 – Professions réglementées' },
            { value: '4', label: 'Volet 4 – Talents d’exception' },
            { value: 'inconnu', label: 'À déterminer avec la profession principale / CNP' }
          ],
          responses.qcPstqStream === 'inconnu' ? 'qc-cnp' : 'qc-french-proof'
        )}

        {currentStep === 'qc-cnp' && (() => {
          const guidance = getPstqCnpGuidance();
          const cnpCode = String(responses.qcCnpCode || '');
          const validCode = /^\d{5}$/.test(cnpCode);
          const cnpSearch = String(responses.qcCnpSearch || '');
          const normalizedQuery = normalizeSearchTerm(cnpSearch.trim());
          const cnpResults = normalizedQuery.length >= 2
            ? CNP_2021.filter((record) => normalizeSearchTerm(`${record.code} ${record.title}`).includes(normalizedQuery)).slice(0, 12)
            : [];

          const selectCnp = (record: CnpRecord) => {
            const suggestedStream = ['0', '1', '2'].includes(record.teer) ? '1' : '2';
            setResponses((previous) => ({
              ...previous,
              qcCnpSearch: `${record.code} — ${record.title}`,
              qcCnpCode: record.code,
              qcPrincipalOccupation: record.title,
              qcPstqStream: suggestedStream
            }));
          };

          return (
            <div className="visa-step-container">
              <div className="visa-step-header">
                <h2 className="visa-step-title">Profession principale et code CNP</h2>
                <p className="visa-step-subtitle">Recherchez directement dans la CNP 2021 intégrée au CRM. Sélectionnez le métier correspondant aux tâches réellement exercées.</p>
              </div>

              <div className="visa-form-group">
                <label htmlFor="qc-cnp-search">Rechercher un métier ou un code CNP</label>
                <input
                  id="qc-cnp-search"
                  type="text"
                  value={cnpSearch}
                  onChange={(event) => handleResponse('qcCnpSearch', event.target.value)}
                  placeholder="Ex. soudeur, technicien comptable ou 72106"
                  className="visa-input"
                />
                {cnpSearch.trim().length >= 2 && (
                  <div className="visa-cnp-results" role="listbox" aria-label="Résultats de la recherche CNP">
                    {cnpResults.length > 0 ? cnpResults.map((record) => (
                      <button type="button" key={record.code} className="visa-cnp-result" onClick={() => selectCnp(record)}>
                        <strong>{record.code}</strong>
                        <span>{record.title}</span>
                        <em>FEER {record.teer}</em>
                      </button>
                    )) : <p className="visa-cnp-empty">Aucun métier trouvé. Essayez un autre mot-clé ou le code CNP à 5 chiffres.</p>}
                  </div>
                )}
              </div>

              <div className="visa-form-group">
                <label htmlFor="qc-principal-occupation">Profession principale sélectionnée</label>
                <input
                  id="qc-principal-occupation"
                  type="text"
                  value={responses.qcPrincipalOccupation || ''}
                  onChange={(event) => handleResponse('qcPrincipalOccupation', event.target.value)}
                  placeholder="Sélectionnez un résultat ou ajustez le métier"
                  className="visa-input"
                />
              </div>

              <div className="visa-form-group">
                <label htmlFor="qc-cnp-code">Code CNP 2021 sélectionné</label>
                <input
                  id="qc-cnp-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={cnpCode}
                  onChange={(event) => handleResponse('qcCnpCode', event.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="Le code est rempli après sélection"
                  className="visa-input"
                />
              </div>

              {guidance && (
                <div className="visa-info-box">
                  <strong>Orientation automatique — FEER {guidance.teer}</strong>
                  <p>{guidance.recommendation}</p>
                  <p>{guidance.detail}</p>
                </div>
              )}

              {cnpCode && !validCode && <p className="visa-error-message">Le code CNP doit contenir exactement 5 chiffres.</p>}

              <div className="visa-step-actions">
                <button onClick={() => nextStep('qc-french-proof')} disabled={!responses.qcPrincipalOccupation || !validCode} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
              </div>
            </div>
          );
        })()}

        {renderEEChoiceStep(
          'qc-peq-worker-experience',
          'Avez-vous l’expérience de travail au Québec requise pour le PEQ – Travailleur temporaire ?',
          'qcPeqWorkerExperience',
          [{ value: 'oui', label: 'Oui', description: 'À valider : emploi légal, rémunéré, à temps plein et expérience admissible selon les règles en vigueur.' }, { value: 'non', label: 'Non / à vérifier', description: 'La liste sera fournie à titre de préparation; aucune admissibilité n’est confirmée.' }],
          'qc-french-proof'
        )}

        {renderEEChoiceStep(
          'qc-peq-graduate-credential',
          'Détenez-vous un diplôme admissible obtenu au Québec ?',
          'qcPeqGraduateCredential',
          [{ value: 'oui', label: 'Oui', description: 'Le diplôme, les relevés de notes et les conditions de réception devront être vérifiés.' }, { value: 'non', label: 'Non / à vérifier', description: 'La liste sera fournie à titre de préparation; aucune admissibilité n’est confirmée.' }],
          'qc-french-proof'
        )}

        {renderEEChoiceStep(
          'qc-french-proof',
          'Avez-vous une preuve de connaissance du français ?',
          'qcFrenchProof',
          [{ value: 'oui', label: 'Oui', description: 'Joignez uniquement une preuve reconnue par le MIFI lorsqu’elle est demandée pour votre volet.' }, { value: 'non', label: 'Non / à vérifier', description: 'Les exigences linguistiques dépendent du programme, du volet et de l’invitation.' }],
          'qc-family'
        )}

        {currentStep === 'qc-family' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Avez-vous des membres de famille à déclarer dans la demande ?</h2>
              <p className="visa-step-subtitle">Incluez les personnes à charge ou le conjoint, selon votre situation et les définitions du MIFI.</p>
            </div>
            <div className="visa-grid">
              {['oui', 'non'].map((value) => (
                <label key={value} className="visa-option-card">
                  <input type="radio" name="qcFamily" value={value} checked={responses.qcHasAccompanyingFamily === value} onChange={(e) => handleResponse('qcHasAccompanyingFamily', e.target.value)} className="visa-radio" />
                  <div className="visa-card-content"><div><h3 className="visa-card-title">{value === 'oui' ? 'Oui' : 'Non'}</h3><p className="visa-card-description">{value === 'oui' ? 'Les documents d’état civil pertinents seront ajoutés.' : 'Aucun document familial additionnel ne sera ajouté par défaut.'}</p></div></div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={generateDocumentList} disabled={!responses.qcHasAccompanyingFamily} className="visa-button-primary">Générer la liste <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-residence' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Avez-vous résidé plus de 6 mois dans un autre pays ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeResidedAbroad" value="oui" onChange={(e) => handleResponse('eeResidedAbroad', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeResidedAbroad" value="non" onChange={(e) => handleResponse('eeResidedAbroad', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('ee-french-test')} disabled={!responses.eeResidedAbroad} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-french-test' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Avez-vous passé un test de langue en français ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeHasFrenchTest" value="oui" onChange={(e) => handleResponse('eeHasFrenchTest', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeHasFrenchTest" value="non" onChange={(e) => handleResponse('eeHasFrenchTest', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('ee-english-test')} disabled={!responses.eeHasFrenchTest} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-english-test' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Avez-vous passé un test de langue en anglais ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeHasEnglishTest" value="oui" onChange={(e) => handleResponse('eeHasEnglishTest', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeHasEnglishTest" value="non" onChange={(e) => handleResponse('eeHasEnglishTest', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('ee-family-in-canada')} disabled={!responses.eeHasEnglishTest} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-family-in-canada' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Avez-vous un membre de la famille au Canada (père/mère/frère/sœur) ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeHasFamilyInCanada" value="oui" onChange={(e) => handleResponse('eeHasFamilyInCanada', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeHasFamilyInCanada" value="non" onChange={(e) => handleResponse('eeHasFamilyInCanada', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('ee-program')} disabled={!responses.eeHasFamilyInCanada} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {renderEEChoiceStep(
          'ee-program',
          'Sous quel programme Entrée express présentez-vous votre demande ?',
          'eeProgram',
          [
            { value: 'fsw', label: 'Travailleurs qualifiés (fédéral)' },
            { value: 'cec', label: 'Catégorie de l’expérience canadienne' },
            { value: 'fst', label: 'Métiers spécialisés (fédéral)' }
          ],
          'ee-education'
        )}

        {renderEEChoiceStep(
          'ee-education',
          'Quelle preuve d’études utiliserez-vous pour votre demande ?',
          'eeEducationProof',
          [
            { value: 'canadian', label: 'Diplôme ou attestation d’études canadien' },
            { value: 'eca', label: 'Évaluation des diplômes d’études (EDE) pour des études hors Canada' },
            { value: 'none', label: 'Aucune preuve d’études utilisée' }
          ],
          'ee-job-offer'
        )}

        {renderEEChoiceStep(
          'ee-job-offer',
          'Avez-vous une offre d’emploi écrite d’un employeur canadien ?',
          'eeHasJobOffer',
          [
            { value: 'oui', label: 'Oui' },
            { value: 'non', label: 'Non' }
          ],
          'ee-provincial-nomination'
        )}

        {renderEEChoiceStep(
          'ee-provincial-nomination',
          'Avez-vous une désignation provinciale ?',
          'eeHasProvincialNomination',
          [
            { value: 'oui', label: 'Oui' },
            { value: 'non', label: 'Non' }
          ],
          'ee-trade-certificate'
        )}

        {renderEEChoiceStep(
          'ee-trade-certificate',
          'Avez-vous un certificat de qualification dans un métier spécialisé canadien ?',
          'eeHasTradeCertificate',
          [
            { value: 'oui', label: 'Oui' },
            { value: 'non', label: 'Non' }
          ],
          'ee-funds-exemption'
        )}

        {renderEEChoiceStep(
          'ee-funds-exemption',
          'Êtes-vous exempté(e) de la preuve de fonds ?',
          'eeFundsExempt',
          [
            { value: 'oui', label: 'Oui', description: 'Vous travaillez légalement au Canada et avez une offre d’emploi valide, ou vous êtes admissible à une exemption.' },
            { value: 'non', label: 'Non', description: 'Une preuve de fonds sera ajoutée à votre liste.' }
          ],
          'ee-representative'
        )}

        {renderEEChoiceStep(
          'ee-representative',
          'Utilisez-vous les services d’un représentant autorisé ?',
          'eeUsesRepresentative',
          [
            { value: 'oui', label: 'Oui' },
            { value: 'non', label: 'Non' }
          ],
          'ee-job-count'
        )}

        {currentStep === 'ee-job-count' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Combien d'emplois avez-vous occupé pendant les 10 dernières années ?</h2>
              <p className="visa-step-subtitle">Indiquez le nombre total d'emplois que vous avez occupés</p>
            </div>
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
              borderRadius: '16px',
              border: '2px solid #90caf9'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #2196f3 0%, #9c27b0 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1rem',
                  fontSize: '24px',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                }}>
                  💼
                </div>
                <div>
                  <label style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#1976d2',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Nombre d'emplois occupés
                  </label>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#5e35b1',
                    margin: 0
                  }}>
                    Comptez tous vos emplois des 10 dernières années
                  </p>
                </div>
              </div>
              <input
                type="number"
                min="0"
                max="20"
                value={responses.eeJobCount || ''}
                onChange={(e) => handleResponse('eeJobCount', e.target.value)}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  border: '2px solid #90caf9',
                  borderRadius: '12px',
                  background: 'white',
                  color: '#1976d2',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
                }}
                placeholder="Entrez"
                onFocus={(e) => {
                  e.target.style.borderColor = '#2196f3';
                  e.target.style.boxShadow = '0 4px 16px rgba(33, 150, 243, 0.3)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#90caf9';
                  e.target.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.1)';
                }}
              />
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('ee-job-details-0')} disabled={!responses.eeJobCount} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep.startsWith('ee-job-details-') && (
           (() => {
             const index = parseInt(currentStep.replace('ee-job-details-', ''));
             return (
               <div className="visa-step-container">
                 <div className="visa-step-header">
                   <h2 className="visa-step-title">Détails pour l'emploi n°{index + 1}</h2>
                   <p className="visa-step-subtitle">Étiez-vous salarié ou entrepreneur pour ce poste ?</p>
                 </div>
                 <div className="visa-grid">
                   <label className="visa-option-card">
                     <input
                       type="radio"
                       name={`eeJobType-${index}`}
                       value="salarie"
                       checked={responses.eeJobs?.[index]?.type === 'salarie'}
                       onChange={() => setEEJobType(index, 'salarie')}
                     />
                     <div className="visa-card-content">
                       <h3 className="visa-card-title">Salarié</h3>
                     </div>
                   </label>
                   <label className="visa-option-card">
                     <input
                       type="radio"
                       name={`eeJobType-${index}`}
                       value="entrepreneur"
                       checked={responses.eeJobs?.[index]?.type === 'entrepreneur'}
                       onChange={() => setEEJobType(index, 'entrepreneur')}
                     />
                     <div className="visa-card-content">
                       <h3 className="visa-card-title">Entrepreneur</h3>
                     </div>
                   </label>
                 </div>
                 <div className="visa-step-actions">
                   <button onClick={() => {
                     const nextIndex = index + 1;
                     if (responses.eeJobCount && nextIndex < parseInt(responses.eeJobCount)) {
                       nextStep(`ee-job-details-${nextIndex}`);
                     } else {
                       nextStep('ee-children-question');
                     }
                   }} disabled={!responses.eeJobs?.[index]?.type} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
                 </div>
               </div>
             );
           })()
         )}

         {currentStep === 'ee-children-question' && (
           <div className="visa-step-container">
             <div className="visa-step-header">
               <h2 className="visa-step-title">Combien d'enfants avez-vous ?</h2>
               <p className="visa-step-subtitle">Indiquez le nombre total d'enfants que vous avez</p>
             </div>
             <div style={{
               marginTop: '2rem',
               padding: '1.5rem',
               background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
               borderRadius: '16px',
               border: '2px solid #90caf9'
             }}>
               <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                 <div style={{
                   width: '48px',
                   height: '48px',
                   background: 'linear-gradient(135deg, #2196f3 0%, #9c27b0 100%)',
                   borderRadius: '12px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   marginRight: '1rem',
                   fontSize: '24px',
                   boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                 }}>
                   👶
                 </div>
                 <div>
                   <label style={{
                     fontSize: '1.1rem',
                     fontWeight: '600',
                     color: '#1976d2',
                     marginBottom: '4px',
                     display: 'block'
                   }}>
                     Nombre d'enfants
                   </label>
                   <p style={{
                     fontSize: '0.875rem',
                     color: '#5e35b1',
                     margin: 0
                   }}>
                     Incluez tous vos enfants (qui vous accompagnent ou non)
                   </p>
                 </div>
               </div>
               <input
                 type="number"
                 min="0"
                 max="10"
                 value={responses.eeHasChildren || ''}
                 onChange={(e) => handleResponse('eeHasChildren', e.target.value)}
                 style={{
                   width: '100%',
                   padding: '16px 20px',
                   fontSize: '1.1rem',
                   fontWeight: '600',
                   border: '2px solid #90caf9',
                   borderRadius: '12px',
                   background: 'white',
                   color: '#1976d2',
                   textAlign: 'center',
                   transition: 'all 0.3s ease',
                   boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
                 }}
                 placeholder="Entrez"
                 onFocus={(e) => {
                   e.target.style.borderColor = '#2196f3';
                   e.target.style.boxShadow = '0 4px 16px rgba(33, 150, 243, 0.3)';
                 }}
                 onBlur={(e) => {
                   e.target.style.borderColor = '#90caf9';
                   e.target.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.1)';
                 }}
               />
             </div>
             <div className="visa-step-actions">
               <button onClick={() => nextStep('ee-spouse-question')} disabled={responses.eeHasChildren === undefined || responses.eeHasChildren === ''} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
             </div>
           </div>
         )}

        {currentStep === 'ee-spouse-question' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Quelle est votre situation familiale ?</h2>
            </div>
            <div className="visa-grid">
              {[
                { value: 'single', label: 'Célibataire' },
                { value: 'married', label: 'Marié(e)' },
                { value: 'common-law', label: 'Union de fait' },
                { value: 'divorced', label: 'Divorcé(e)' },
                { value: 'widowed', label: 'Veuf/Veuve' }
              ].map((status) => (
                <label key={status.value} className="visa-option-card">
                  <input
                    type="radio"
                    name="eeMaritalStatus"
                    value={status.value}
                    checked={responses.eeMaritalStatus === status.value}
                    onChange={(e) => handleResponse('eeMaritalStatus', e.target.value)}
                    className="visa-radio"
                  />
                  <div className="visa-card-content"><h3 className="visa-card-title">{status.label}</h3></div>
                </label>
              ))}
            </div>
            <div className="visa-step-actions">
              <button onClick={() => {
                if (responses.eeMaritalStatus === 'married' || responses.eeMaritalStatus === 'common-law') {
                  nextStep('ee-spouse-accompanying');
                } else if (hasPositiveCount(responses.eeHasChildren)) {
                  nextStep('ee-children-accompanying');
                } else {
                  generateDocumentList();
                }
              }} disabled={!responses.eeMaritalStatus} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-spouse-accompanying' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Votre époux/épouse va vous accompagner au Canada ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseAccompanying" value="oui" onChange={(e) => handleResponse('eeSpouseAccompanying', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseAccompanying" value="non" onChange={(e) => handleResponse('eeSpouseAccompanying', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => {
                if (responses.eeSpouseAccompanying === 'oui') {
                  nextStep('ee-spouse-residence');
                } else if (hasPositiveCount(responses.eeHasChildren)) {
                  nextStep('ee-children-accompanying');
                } else {
                  generateDocumentList();
                }
              }} disabled={!responses.eeSpouseAccompanying} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-children-accompanying' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Vos enfants vont-ils vous accompagner au Canada ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeChildrenAccompanying" value="oui" onChange={(e) => handleResponse('eeChildrenAccompanying', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeChildrenAccompanying" value="non" onChange={(e) => handleResponse('eeChildrenAccompanying', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => {
                if (responses.eeChildrenAccompanying === 'oui') {
                  nextStep('ee-children-count');
                } else {
                  generateDocumentList();
                }
              }} disabled={!responses.eeChildrenAccompanying} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-children-count' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Combien d'enfants vous accompagnent ?</h2>
              <p className="visa-step-subtitle">Précisez le nombre exact d'enfants qui voyageront avec vous</p>
            </div>
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
              borderRadius: '16px',
              border: '2px solid #90caf9'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #2196f3 0%, #9c27b0 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1rem',
                  fontSize: '24px',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                }}>
                  👶
                </div>
                <div>
                  <label style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#1976d2',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Nombre d'enfants qui vous accompagnent
                  </label>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#5e35b1',
                    margin: 0
                  }}>
                    Entrez un nombre entre 0 et 10
                  </p>
                </div>
              </div>
              <input
                type="number"
                min="0"
                max="10"
                value={responses.eeChildrenCount || ''}
                onChange={(e) => handleResponse('eeChildrenCount', e.target.value)}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  border: '2px solid #90caf9',
                  borderRadius: '12px',
                  background: 'white',
                  color: '#1976d2',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
                }}
                placeholder="Entrez"
                onFocus={(e) => {
                  e.target.style.borderColor = '#2196f3';
                  e.target.style.boxShadow = '0 4px 16px rgba(33, 150, 243, 0.3)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#90caf9';
                  e.target.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.1)';
                }}
              />
            </div>
            <div className="visa-step-actions">
              <button onClick={() => {
                if (hasPositiveCount(responses.eeChildrenCount)) {
                  nextStep('ee-children-single-parent');
                } else {
                  generateDocumentList();
                }
              }} disabled={responses.eeChildrenCount === undefined || responses.eeChildrenCount === ''} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-children-single-parent' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Voyagez-vous seul(e) avec vos enfants ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeSingleParentTravel" value="oui" onChange={(e) => handleResponse('eeSingleParentTravel', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeSingleParentTravel" value="non" onChange={(e) => handleResponse('eeSingleParentTravel', e.target.value)} className="visa-radio" />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => generateDocumentList()} disabled={!responses.eeSingleParentTravel} className="visa-button-primary">Générer la liste</button>
            </div>
          </div>
        )}

        {currentStep === 'additional-docs' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Documents complémentaires (optionnel)</h2>
              <p className="visa-step-subtitle">Ajoutez des documents complémentaires si nécessaire.</p>
            </div>
            
            <div className="visa-grid">
              {[
                { value: 'frais_scolarite', label: 'Preuve de paiement des frais de scolarité (total ou partiel)' },
                { value: 'certificat_garantie', label: 'Certificat de garantie au Canada (pour les frais de subsistance de la première année d\'études)' },
                { value: 'invitation_bail', label: 'Lettre d\'invitation ou contrat de bail' },
                { value: 'promesse_embauche', label: 'Promesse d\'embauche hors Canada après l\'obtention du diplôme' },
                { value: 'reussites', label: 'Toute autre preuve de réussite de l\'étudiant (personnelle, associative, professionnelle ou scolaire)' },
                { value: 'attestation_blocage', label: 'Attestation de blocage des fonds pour les frais de subsistance de la première année' }
              ].map(option => (
                <label key={option.value} className="visa-option-card">
                  <input
                    type="checkbox"
                    onChange={(e) => handleMultipleChoice('additionalDocs', option.value, e.target.checked)}
                    className="visa-checkbox"
                  />
                  <div className="visa-card-content">
                    <div className="visa-card-icon">📄</div>
                    <div>
                      <h3 className="visa-card-title">{option.label}</h3>
                      <p className="visa-card-description">Indiquez les documents complémentaires que vous souhaitez ajouter.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="visa-step-actions">
              <button
                onClick={generateDocumentList}
                className="visa-button-primary"
              >
                <span className="button-icon">✓</span>
                Générer la liste de documents
              </button>
            </div>
          </div>
        )}

        {currentStep === 'ee-spouse-residence' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Votre époux/épouse a-t-il/elle résidé plus de 6 mois dans un autre pays ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseResidedAbroad" value="oui" onChange={(e) => handleResponse('eeSpouseResidedAbroad', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseResidedAbroad" value="non" onChange={(e) => handleResponse('eeSpouseResidedAbroad', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('ee-spouse-french-test')} disabled={!responses.eeSpouseResidedAbroad} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-spouse-french-test' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Votre époux/épouse a-t-il/elle passé un test de langue en français ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseHasFrenchTest" value="oui" onChange={(e) => handleResponse('eeSpouseHasFrenchTest', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseHasFrenchTest" value="non" onChange={(e) => handleResponse('eeSpouseHasFrenchTest', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('ee-spouse-english-test')} disabled={!responses.eeSpouseHasFrenchTest} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-spouse-english-test' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Votre époux/épouse a-t-il/elle passé un test de langue en anglais ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseHasEnglishTest" value="oui" onChange={(e) => handleResponse('eeSpouseHasEnglishTest', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseHasEnglishTest" value="non" onChange={(e) => handleResponse('eeSpouseHasEnglishTest', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => nextStep('ee-spouse-family-in-canada')} disabled={!responses.eeSpouseHasEnglishTest} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}

        {currentStep === 'ee-spouse-family-in-canada' && (
          <div className="visa-step-container">
            <div className="visa-step-header">
              <h2 className="visa-step-title">Votre époux/épouse a-t-il/elle un membre de la famille au Canada (père/mère/frère/sœur) ?</h2>
            </div>
            <div className="visa-grid">
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseHasFamilyInCanada" value="oui" onChange={(e) => handleResponse('eeSpouseHasFamilyInCanada', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Oui</h3></div>
              </label>
              <label className="visa-option-card">
                <input type="radio" name="eeSpouseHasFamilyInCanada" value="non" onChange={(e) => handleResponse('eeSpouseHasFamilyInCanada', e.target.value)} />
                <div className="visa-card-content"><h3 className="visa-card-title">Non</h3></div>
              </label>
            </div>
            <div className="visa-step-actions">
              <button onClick={() => {
                if (hasPositiveCount(responses.eeHasChildren)) {
                  nextStep('ee-children-accompanying');
                } else {
                  generateDocumentList();
                }
              }} disabled={!responses.eeSpouseHasFamilyInCanada} className="visa-button-primary">Continuer <span className="button-arrow">→</span></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisaDocumentGenerator;
